import React, { useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import _ from "lodash";

import {
  Drawer,
  Dropdown,
  Input,
  Flex,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Button,
} from "antd";
import {
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  EditFilled,
  PlusOutlined,
  DownOutlined,
  RedoOutlined,
  CopyOutlined,
  ClearOutlined,
  ArrowLeftOutlined,
  InfoCircleFilled,
  CloseOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import { v4 as uuidv4 } from "uuid";
import { jsonrepair } from "jsonrepair";
import { flushSync } from "react-dom";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { Download } from 'react-bootstrap-icons';

import { ScenariosContext } from "../index";
import { ActionButton } from "../../../components/elements/Button";
import { DropdownMenu } from "../../../components/elements/DropdownMenu";
import { SortableList } from "../SortableList";
import {
  defaultParameterNotation,
  transformDataObject,
  NestedObjectTable,
} from "../../../components/elements/JsonObjTable";
import configService from "../../../services/config.service";
import datalayerguardService from "../../../services/datalayerguard.service.js";
import { AuthContext } from "../../../services/auth.service";
import { defaultMenuProps } from "../defaultEventTemplates";
import { validateDomainTLD, getTLDErrorMessage } from "./utils";
import { ImportStepsModal } from "../ImportStepsModal";
import { SaveAsComponentModal } from "../SaveAsComponentModal";
import { ScreencastWindow } from "../../../components/elements/screencastWindow/index.js";
import "./styles.scss";

const { TextArea } = Input;

const generateUniqueId = () => uuidv4();

const findEventTemplateValue = (data) => {
  if (!data || typeof data !== "object") {
    return undefined;
  }

  if (data.key === "event" && data.value != null) {
    return data.value;
  }

  const children = Array.isArray(data) ? data : Object.values(data);

  for (const child of children) {
    const result = findEventTemplateValue(child);
    if (result !== undefined) {
      return result;
    }
  }

  return undefined;
};

const getMonitoredEventName = (step) => {
  if (step?.value) {
    return step.value;
  }

  return findEventTemplateValue(step?.dataLayerObjToFind);
};

const collectMonitoredEventSteps = (steps, resolveComponentSteps, visited = new Set()) => {
  if (!Array.isArray(steps)) return [];

  return steps.flatMap((step) => {
    if (step?.blockType !== "component_ref") {
      return [step];
    }

    const componentId = step.componentId;
    if (!componentId || visited.has(componentId)) return [];

    visited.add(componentId);
    const componentSteps = collectMonitoredEventSteps(
      resolveComponentSteps(componentId),
      resolveComponentSteps,
      visited,
    );
    visited.delete(componentId);
    return componentSteps;
  });
};

// Re-issue blockIds on any block_ref/scenario_link that is missing one or
// shares an id with an earlier item in the same list. Persisted scenarios
// can contain colliding blockIds (legacy data / older insert paths), which
// breaks React keys and dnd-kit's SortableContext.
const ensureUniqueBlockIds = (steps) => {
  if (!Array.isArray(steps)) return steps;
  const seen = new Set();
  let mutated = false;
  const next = steps.map((s) => {
    if (!s?.blockType) return s;
    if (!s.blockId || seen.has(s.blockId)) {
      mutated = true;
      const fresh = { ...s, blockId: generateUniqueId() };
      seen.add(fresh.blockId);
      return fresh;
    }
    seen.add(s.blockId);
    return s;
  });
  return mutated ? next : steps;
};

const DISCLAIMER = 'This document contains a snapshot of your DataLayer Guard test results. For the most up-to-date information, please log in to your account at:';
const DISCLAIMER_URL = 'https://portal.code-cube.io';
const DISCLAIMER_LINK_TEXT = 'portal.code-cube.io';

const downloadDLGResultsAsPDF = ({ scenarios, companyInfo, logoBase64, logoAspectRatio, templatesToObject }) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const HEADER_HEIGHT = 22;
  const CONTENT_START = HEADER_HEIGHT + 8;
  const FOOTER_HEIGHT = 20;
  const reportTitle = 'DLG Test Results';

  const addLogoHeader = (d, title) => {
    const LOGO_HEIGHT = 12;
    const logoWidth = logoBase64 ? Math.min(LOGO_HEIGHT * logoAspectRatio, 55) : 0;
    d.setFillColor(9, 0, 61);
    d.rect(0, 0, pageWidth, 22, 'F');
    if (logoBase64) {
      d.addImage(logoBase64, 'PNG', 6, 5, logoWidth, LOGO_HEIGHT, 'cc-logo');
    }
    d.setFontSize(10);
    d.setTextColor(255, 255, 255);
    d.text(title, pageWidth - 6, 12, { align: 'right' });
  };

  const addDisclaimer = (d, x, startY, maxWidth) => {
    d.setFontSize(7);
    d.setTextColor(149, 165, 166);
    const textLines = d.splitTextToSize(DISCLAIMER, maxWidth);
    d.text(textLines, x, startY);
    const urlY = startY + textLines.length * 3.5;
    d.setTextColor(149, 165, 166);
    d.text(DISCLAIMER_LINK_TEXT, x, urlY);
    const urlWidth = d.getTextWidth(DISCLAIMER_LINK_TEXT);
    d.link(x, urlY - 2.5, urlWidth, 3.5, { url: DISCLAIMER_URL });
  };

  const addNewPage = (fontSize, r, g, b) => {
    doc.addPage();
    addLogoHeader(doc, reportTitle);
    doc.setFontSize(fontSize);
    doc.setTextColor(r, g, b);
    return CONTENT_START;
  };

  addLogoHeader(doc, reportTitle);
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`, 10, CONTENT_START);
  doc.text(`Company: ${companyInfo?.name || 'N/A'}`, 10, CONTENT_START + 6);
  doc.text(`Total Scenarios: ${scenarios.length}`, 10, CONTENT_START + 12);

  let yPosition = CONTENT_START + 22;
  doc.setDrawColor(9, 0, 61);
  doc.setLineWidth(0.3);
  doc.line(10, yPosition, pageWidth - 10, yPosition);
  yPosition += 8;

  scenarios.forEach(([id, data]) => {
    const name = data.scenarioTitle || Object.values(data.steps || {})[0]?.stepScenario || id;
    if (yPosition > pageHeight - FOOTER_HEIGHT - 20) yPosition = addNewPage(11, 9, 0, 61);
    doc.setFontSize(11);
    doc.setTextColor(9, 0, 61);
    doc.text(`Scenario: ${name}`, 10, yPosition);
    yPosition += 7;

    Object.entries(data.steps || {}).forEach(([key, step], i) => {
      if (yPosition > pageHeight - FOOTER_HEIGHT) yPosition = addNewPage(8, 44, 62, 80);
      doc.setFontSize(8);
      doc.setTextColor(44, 62, 80);
      const isWarn = step.stepSeverity === 'warning';
      const statusIcon = step.stepStatus === 'success' ? '✓' : isWarn ? '!' : '✗';
      const isObject = step.stepType === 'collect_dl_object' || step.stepType === 'collect_dl_before_navigation';
      let stepText = `  ${i + 1}. [${statusIcon}] ${step.stepType}`;
      if (step.stepError) stepText += isWarn ? `  — Warning` : `  — Error`;
      doc.text(stepText, 10, yPosition);
      yPosition += 5;

      if (isObject && step.stepValue?.length > 0) {
        const valLines = doc.splitTextToSize(JSON.stringify(templatesToObject(step.stepValue), null, 2), pageWidth - 25);
        valLines.forEach(line => {
          if (yPosition > pageHeight - FOOTER_HEIGHT) yPosition = addNewPage(7, 80, 80, 80);
          doc.setFontSize(7);
          doc.setTextColor(80, 80, 80);
          doc.text(line, 15, yPosition);
          yPosition += 3.5;
        });
      }
    });

    if (data.errors?.length > 0) {
      if (yPosition > pageHeight - FOOTER_HEIGHT - 10) yPosition = addNewPage(8, 192, 57, 43);
      doc.setFontSize(8);
      doc.setTextColor(192, 57, 43);
      doc.text('  Errors:', 10, yPosition);
      yPosition += 5;
      data.errors.forEach(err => {
        if (yPosition > pageHeight - FOOTER_HEIGHT) yPosition = addNewPage(8, 192, 57, 43);
        doc.splitTextToSize(`  • ${err}`, pageWidth - 20).forEach(line => {
          doc.text(line, 10, yPosition);
          yPosition += 4;
        });
      });
    }
    yPosition += 5;
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addDisclaimer(doc, 10, pageHeight - 15, pageWidth - 20);
    doc.setFontSize(7);
    doc.setTextColor(149, 165, 166);
    doc.text(`Page ${i} of ${totalPages}  |  code-cube.io`, pageWidth / 2, pageHeight - 3, { align: 'center' });
  }

  doc.save(`dlg-test-results-${dayjs().format('YYYY-MM-DD')}.pdf`);
};

const downloadDLGResultsAsXLSX = async ({ scenarios, companyInfo, logoBase64, logoAspectRatio, templatesToObject }) => {
  const applyHeaderRow = (row) => {
    row.height = 18;
    row.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF09003D' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10, name: 'Calibri' };
      cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false };
    });
  };

  const applyDataRow = (row, isAlt) => {
    row.eachCell({ includeEmpty: true }, cell => {
      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF3FB' } };
      }
      cell.alignment = { vertical: 'middle', wrapText: false };
      cell.font = { size: 9, name: 'Calibri' };
    });
  };

  const addBranding = async (wb, ws, title, metaLines, headerColCount = 6) => {
    ws.getRow(1).height = 59;
    for (let col = 1; col <= headerColCount; col++) {
      ws.getCell(1, col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF09003D' } };
    }
    if (logoBase64) {
      const base64Data = logoBase64.includes(',') ? logoBase64.split(',')[1] : logoBase64;
      const logoImgId = wb.addImage({ base64: base64Data, extension: 'png' });
      const logoHeight = 50;
      const logoWidth = logoHeight * logoAspectRatio;
      ws.addImage(logoImgId, { tl: { col: 0, row: 0 }, ext: { width: logoWidth, height: logoHeight }, editAs: 'oneCell' });
    }
    ws.getRow(2).height = 22;
    const titleCell = ws.getCell('A2');
    titleCell.value = title;
    titleCell.font = { bold: true, size: 13, color: { argb: 'FF09003D' }, name: 'Calibri' };
    metaLines.forEach((line, i) => {
      const cell = ws.getCell(`A${3 + i}`);
      cell.value = line;
      cell.font = { size: 9, color: { argb: 'FF777777' }, name: 'Calibri' };
    });
    return 3 + metaLines.length + 1;
  };

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Code-Cube.io';
  const ws = wb.addWorksheet('Test Results');

  const metaLines = [
    `Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
    `Company: ${companyInfo?.name || 'N/A'}`,
  ];
  const dataHeaderRowIdx = await addBranding(wb, ws, 'DLG Test Results', metaLines, 6);

  const headerRow = ws.getRow(dataHeaderRowIdx);
  headerRow.values = ['Scenario', 'Step', 'Type', 'Status', 'Value', 'Error'];
  applyHeaderRow(headerRow);

  ws.getColumn(1).width = 30;
  ws.getColumn(2).width = 8;
  ws.getColumn(3).width = 30;
  ws.getColumn(4).width = 12;
  ws.getColumn(5).width = 50;
  ws.getColumn(6).width = 40;

  let rowIdx = dataHeaderRowIdx + 1;
  let altIdx = 0;
  scenarios.forEach(([id, data]) => {
    const name = data.scenarioTitle || Object.values(data.steps || {})[0]?.stepScenario || id;
    Object.entries(data.steps || {}).forEach(([key, step]) => {
      const isObject = step.stepType === 'collect_dl_object' || step.stepType === 'collect_dl_before_navigation';
      const valueStr = isObject && step.stepValue?.length > 0
        ? JSON.stringify(templatesToObject(step.stepValue))
        : step.stepValue ? JSON.stringify(step.stepValue) : '';
      const row = ws.getRow(rowIdx++);
      const status = step.stepSeverity === 'warning' ? 'warning' : step.stepStatus;
      row.values = [name, key, step.stepType, status, valueStr, step.stepError || ''];
      applyDataRow(row, altIdx % 2 === 1);
      row.height = 20;
      altIdx++;
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dlg-test-results-${dayjs().format('YYYY-MM-DD')}.xlsx`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const templateElemsList = (items) => {
  if (!items) {
    return [
      defaultParameterNotation({
        key: "",
        type: "string",
        required: true,
        exatclyMatch: false,
        value: "",
      }),
    ];
  }
  switch (items.length) {
    case 0:
      return [
        defaultParameterNotation({
          key: "",
          type: "string",
          required: true,
          exatclyMatch: false,
          value: "",
        }),
      ];
    default:
      return items;
  }
};

const scenarioStartPageMatchTypes = [
  {
    label: "exact matches",
    key: "exact_matches",
  },
  {
    label: "matches regex",
    key: "matches_regex",
  },
  {
    label: "contains",
    key: "contains",
  },
];

const scenarioStartPageTypes = [
  {
    label: "hostname",
    key: "hostname",
  },
  {
    label: "page url",
    key: "page_url",
  },
  {
    label: "page path",
    key: "page_path",
  },
];

const defaultScenarioObject = () => ({
  scenarioID: generateUniqueId(),
  scenarioTitle: "",
  active: false,
  urlStartingPoint: "",
  urlStartingPointMatchType: "",
  cookiesAcceptance: {
    active: false,
    cookiesPassed: false,
    steps: [],
  },
  loginInfo: {
    required: false,
    isLoggedIn: false,
    steps: [],
  },
  steps: [],
});

const ConfirmationModalWindow = (props) => {
  const {
    open = false,
    onOk = () => {},
    onCancel = () => {},
    text = "",
  } = props;

  return (
    <Modal
      centered
      title={"Confirm action"}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      okType="saveChanges"
      footer={(_, { OkBtn, CancelBtn }) => (
        <Flex gap={5}>
          <CancelBtn />
          <OkBtn />
        </Flex>
      )}
    >
      {text}
    </Modal>
  );
};

const TestScenarioWindowResultsPanel = ({ results }) => {
  const [expanded, setExpanded] = useState({});
  const [expandedStepValues, setExpandedStepValues] = useState({});
  const { companyInfo } = useContext(AuthContext);
  const [logoBase64, setLogoBase64] = useState(null);
  const [logoAspectRatio, setLogoAspectRatio] = useState(5.5);
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const MAX_WIDTH = 440;
      const scale = Math.min(1, MAX_WIDTH / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setLogoBase64(canvas.toDataURL('image/png'));
      setLogoAspectRatio(img.width / img.height);
    };
    img.src = `${process.env.PUBLIC_URL}/code-cube.io-marketing-observability-white-blue.png`;
  }, []);
  const templatesToObject = (templates) => {
    if (!templates) return null;
    return templates.reduce((acc, template) => {
      if ((template.type === "object" && Array.isArray(template.value)) || (template.type === "array" && Array.isArray(template.value))) {
        acc[template.key] = templatesToObject(template.value);
      } else {
        acc[template.key] = template.value !== "" && template.value !== null ? template.value : template.type;
      }
      return acc;
    }, {});
  };
  const toggleStepValue = (key) =>
    setExpandedStepValues((prev) => ({ ...prev, [key]: !prev[key] }));



  // currenly disabled because also the open results button is disabled so this not nessesary
  // if (!results?.scenarios || Object.keys(results.scenarios).length === 0) {
  //   return (
  //     <div className="results-empty-state">
  //       <div className="results-empty-icon">&#9654;</div>
  //       <p>No results yet. Run a test to see results here.</p>
  //     </div>
  //   );
  // }

  if (!results?.scenarios) return null;

  const scenarios = Object.entries(results.scenarios);
  const isCollectStep = (s) => s.stepType === 'collect_dl_object' || s.stepType === 'collect_dl_before_navigation';
  // Operational failures are surfaced as warnings, not errors (mirrors the logs/steps panels).
  const isWarningStep = (s) => s.stepSeverity === "warning";
  const isErrorStep = (s) => s.stepSeverity === "error";
  const totalErrors = scenarios.reduce((acc, [, d]) => acc + Object.values(d.steps || {}).filter(s => isCollectStep(s) && s.stepStatus === "failed" && isErrorStep(s)).length, 0);
  const totalWarnings = scenarios.reduce((acc, [, d]) => acc + Object.values(d.steps || {}).filter(s => isCollectStep(s) && s.stepStatus === "failed" && isWarningStep(s)).length, 0);
  const totalSteps = scenarios.reduce((acc, [, d]) => acc + Object.values(d.steps || {}).filter(s => isCollectStep(s) && s.stepStatus !== "failed").length, 0);

  const toggle = (name) =>
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <div className="scenario-results-panel">
      <div className="scenario-results-header">
        <div className="scenario-results-title">
          <h2>DataLayer Guard test results</h2>
        </div>
        <div className="scenario-results-overview">
          <div className="scenario-results-summary">
            <span className="summary-chip success">{totalSteps} events collected</span>
            {totalErrors > 0 && <span className="summary-chip error">{totalErrors} event{totalErrors !== 1 ? "s" : ""} have errors</span>}
            {totalWarnings > 0 && <span className="summary-chip warning">{totalWarnings} warning{totalWarnings !== 1 ? "s" : ""}</span>}
          </div>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'pdf',
                  label: 'Download PDF',
                  icon: <Download size={14} />,
                  onClick: () => downloadDLGResultsAsPDF({ scenarios, companyInfo, logoBase64, logoAspectRatio, templatesToObject }),
                },
                {
                  key: 'xls',
                  label: 'Download Excel',
                  icon: <Download size={14} />,
                  onClick: () => downloadDLGResultsAsXLSX({ scenarios, companyInfo, logoBase64, logoAspectRatio, templatesToObject }),
                },
              ],
            }}
            placement="bottomRight"
          >
            <div className="text-area-copy-button">
              <Download size={18} />
            </div>
          </Dropdown>
        </div>
      </div>
      <div className="scenario-results-scroll-body">
        <div className="scenario-results-list">
          {scenarios.map(([id, data]) => {
            const isOpen = !!expanded[id];
            const steps = data.steps || {};
            const stepEntries = Object.entries(steps);
            const collectEntries = Object.values(steps).filter(isCollectStep);
            const NBstepErrors = collectEntries.filter(s => s.stepStatus === "failed" && isErrorStep(s)).length;
            const NBstepWarnings = collectEntries.filter(s => s.stepStatus === "failed" && isWarningStep(s)).length;
            const NBcollectSteps = collectEntries.length;
            const title = data.scenarioTitle || Object.values(steps)[0]?.stepScenario || id;
            return (
              <div key={id} className={`scenario-result-item${isOpen ? " scenario-result-item--open" : ""}`}>
                <div className="scenario-result-header" onClick={() => toggle(id)}>
                  <div className="scenario-result-header-left">

                    <span className={`scenario-result-arrow${isOpen ? " scenario-result-arrow--open" : ""}`}>&#9654;</span>
                    <span className="scenario-result-name">{title}</span>
                  </div>
                  <span className="scenario-result-badge">{NBcollectSteps} event{NBcollectSteps !== 1 ? "s" : ""}</span>
                  {NBstepErrors > 0 && <span className="scenario-result-badge error">{NBstepErrors} event{NBstepErrors !== 1 ? "s" : ""} have errors</span>}
                  {NBstepWarnings > 0 && <span className="scenario-result-badge warning">{NBstepWarnings} warning{NBstepWarnings !== 1 ? "s" : ""}</span>}
                </div>
                {isOpen && (
                  <div className="scenario-result-body">
                    {stepEntries.length === 0 ? (
                      <p className="scenario-result-no-steps">No steps recorded.</p>
                    ) : (
                      <div className="scenario-result-steps">
                        {stepEntries.map(([key, step], i) => {
                          const isObject = step.stepType === "collect_dl_object" || step.stepType === "collect_dl_before_navigation";
                          const isWarn = isWarningStep(step);
                          const isError = isErrorStep(step)
                          const valueKey = `${id}-${key}`;
                        const isValueOpen = !!expandedStepValues[valueKey];
                        return (
                          <div key={i} className="scenario-result-step">
                            <div className={`scenario-result-step-num${step.stepStatus !== "success" ? (isWarn ? " scenario-result-step-num--warning" : " scenario-result-step-num--error") : ""}`}>{key}</div>
                            <div className="scenario-result-step-content">
                              {step.stepStatus === "success" ? (
                                <span className="scenario-result-step-msg">
                                  Step {key} successfully performed action: "{step.stepType}"
                                  {isObject && step.stepValue && step.stepValue.length > 0 && (
                                    <span> with value:
                                      <button className={`scenario-result-step-expand${isValueOpen ? " scenario-result-step-expand--open" : ""}`} onClick={() => toggleStepValue(valueKey)}>&#9654;</button>
                                    </span>
                                  )}
                                  {!isObject && step.stepValue && step.stepValue.length > 0 ? ` with value: ${JSON.stringify(step.stepValue)}` : ''}
                                </span>
                              ) : (
                                <span className={`${isError ? "test-console-error" : ""} scenario-result-step-msg `}>
                                  Step {key} {isWarn ? "could not complete" : "failed while performing"} the "{step.stepType}" action
                                  {step.stepValue && step.stepValue.length > 0 && (isObject ? (
                                    <span> with value:
                                      <button className={`scenario-result-step-expand${isValueOpen ? " scenario-result-step-expand--open" : ""}`} onClick={() => toggleStepValue(valueKey)}>&#9654;</button>
                                    </span>
                                  ) : (
                                    ` with the value: ${JSON.stringify(step.stepValue)}`
                                  ))}
                                  {isError && step.stepError ? ` with an error` : '' }
                                </span>
                              )}
                              {isObject && isValueOpen && step.stepValue && step.stepValue.length > 0 && (
                                step.stepStatus === 'success' ? (
                                  <div className="scenario-result-pre succes step-error-found-object">
                                    <span className="step-error-found-object-label">Found object:</span>
                                    <pre className="s">{JSON.stringify(templatesToObject(step.stepValue), null, 2)}</pre>
                                  </div>
                                ) : step.errorInformation?.[0]?.error_type === 'missing_object' || step.errorInformation?.[0]?.error_type === 'missing_datalayer' ? (
                                  <div className="scenario-result-step-value scenario-result-step-value--error">
                                    <p>Expected object:</p>
                                    <pre className="scenario-result-pre error">{JSON.stringify(JSON.parse(step.errorInformation[0].template || 'null'), null, 2)}</pre>
                                  </div>
                                ) : (
                                  <div className="step-error-info">
                                    {(step.errorInformation?.[0]?.parameters || []).map((param, pi) => (
                                      <div key={pi} className="step-error-param">
                                        <div className="step-error-param-header">
                                          <span className="step-error-param-name">{param.parameter_name}</span>
                                          <span className="step-error-param-pill">{param.error}</span>
                                        </div>
                                        {param.description && (
                                          <p className="step-error-param-desc">{param.description}</p>
                                        )}
                                        <div className="step-error-param-comparison">
                                          <div className="step-error-param-col step-error-param-col--expected">
                                            <span className="step-error-param-col-label">Expected</span>
                                            <div className="step-error-param-row"><span>Type</span><code>{param.expected_type || '—'}</code></div>
                                            <div className="step-error-param-row"><span>Value</span><code>{param.expected_value !== '' && param.expected_value != null ? String(param.expected_value) : '—'}</code></div>
                                          </div>
                                          <div className="step-error-param-col step-error-param-col--actual">
                                            <span className="step-error-param-col-label">Actual</span>
                                            <div className="step-error-param-row"><span>Type</span><code>{param.actual_type || '—'}</code></div>
                                            <div className="step-error-param-row"><span>Value</span><code>{param.actual_value != null ? String(param.actual_value) : '—'}</code></div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    {step.errorInformation?.[0]?.actual_object && (
                                      <div className="step-error-found-object">
                                        <span className="step-error-found-object-label">Found object</span>
                                        <pre className="scenario-result-pre error">{JSON.stringify(JSON.parse(step.errorInformation[0].actual_object), null, 2)}</pre>
                                      </div>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {data.collectedData && (
                    <div className="scenario-result-collected">
                      <p className="scenario-result-section-label">Collected Data</p>
                      <pre className="scenario-result-pre">{JSON.stringify(data.collectedData, null, 2)}</pre>
                    </div>
                  )}
                  {data.errors && data.errors.length > 0 && (
                    <div className="scenario-result-errors">
                      <p className="scenario-result-section-label scenario-result-section-label--error">Errors</p>
                      {data.errors.map((err, i) => (
                        <div key={i} className="scenario-result-error-item">{err}</div>
                      ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
// Operational step failures — the test couldn't run a step. Shown as yellow
// warnings; "dataLayer not found" and parameter errors stay red real errors.
const STEP_FAILURE_LOG_TYPES = new Set([
  "error_unable_to_navigate_starting_point",
  "error_unable_to_navigate",
  "error_page_not_loaded",
  "error_step_action_failed",
]);
const TestScenarioWindow = ({testModalOpen, setTestModalOpen, scenarioId = null, scenarios = [], onOpenScenario = null}) => {
  const scrollRefLogs = useRef()
  const scrollRefSteps = useRef()
  const logsBottomRef = useRef(null)
  const stepsBottomRef = useRef(null)
  const [openResults, setOpenResults] = useState(false)
  const [legendOpen, setLegendOpen] = useState(false);
  const [testLogs, setTestLogs] = useState([]);
  const [testLogsSteps, setTestLogsSteps] = useState([]);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [screenCastLive, setScreenCastLive] = useState(false)
  const [screenCastLoading, setScreenCastLoading] = useState(false)
  const [screencastUrl, setScreencastUrl] = useState(null);
  const [showScreencast, setShowScreencast] = useState(false);
  const [testScenarioResults, setTestScenarioResults] = useState({});
  const [totalCollectedDatalayers, setTotalCollectedDatalayers] = useState(0);
  const [totalCollectedErrors, setTotalCollectedErrors] = useState(0);
  const [totalCollectedSteps, setTotalCollectedSteps] = useState(0);
  const [totalCollectedScenarios, setTotalCollectedScenarios] = useState(0);
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);
  const [autoScrollSteps, setAutoScrollSteps] = useState(true);
  const [testProgress, setTestProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [selectedScenarioIds, setSelectedScenarioIds] = useState([]);
  const wsRef = useRef(null);
  const currentScenarioIdRef = useRef(null);
  const scenarioStartCountRef = useRef({});
  // Correlates each failed step with the granular error_* message that precedes
  // its step_error, so the step logs panel classifies operational failures as
  // warnings and real dataLayer errors as errors — matching the logs panel.
  const stepSeverityRef = useRef({}); // `${scenarioTitle}::${stepNumber}` -> "warning" | "error"
  // Warnings seen in the scenario currently running, so its "finished" summary
  // can split the engine's total failure count into errors vs warnings.
  const scenarioWarningsRef = useRef(0);

  useEffect(() => {
    if (testModalOpen) {
      setSelectedScenarioIds(scenarioId ? [scenarioId] : []);
    }
  }, [testModalOpen]);
  const finishTest = async () => {
    wsRef.current = null
    setIsTestRunning(false);
    setOpenResults(true);
  }
  const addTestLog = (type, text, debugInfo = null) => {
    // Operational step failures show as warnings (yellow); real DLG errors stay red.
    const baseType = STEP_FAILURE_LOG_TYPES.has(type) ? "warning" : type.split("_")[0];
    setTestLogs((prev) => [
      ...prev,
      { baseType, text, debugInfo, time: new Date().toLocaleTimeString() },
    ]);
    
    if (baseType === "error") setTotalCollectedErrors((prev) => prev + 1);
    if (baseType === "datalayer") setTotalCollectedDatalayers((prev) => prev + 1);
    if (type === "scenario_finished_successfully" || type === "scenario_finished_with_errors") setTotalCollectedScenarios((prev) => prev + 1);
  };
  const addTestLogStep = (type, text, stepNumber = null, scenarioTitle = null, scenarioId = null) => {
    setTestLogsSteps((prev) => [
      ...prev,
      { type, text, stepNumber, scenarioTitle, scenarioId, time: new Date().toLocaleTimeString() },
    ]);
    setTotalCollectedSteps((prev) => prev + 1);
  };

  const startTest = async () => {
    setTestLogs([]);
    setTestLogsSteps([]);
    setTotalCollectedDatalayers(0);
    setTotalCollectedErrors(0);
    setTotalCollectedSteps(0);
    setTotalCollectedScenarios(0);
    setTestScenarioResults({});
    setTestProgress({ current: 0, total: 0, percentage: 0 });
    setOpenResults(false);
    setScreencastUrl(null);
    setScreenCastLive(false);
    setScreenCastLoading(true);
    setIsTestRunning(true);
    currentScenarioIdRef.current = null;
    scenarioStartCountRef.current = {};
    stepSeverityRef.current = {};
    scenarioWarningsRef.current = 0;
    try {
      const { ws } = await datalayerguardService.startDatalayerGuard({
        scenarioIds: selectedScenarioIds.length > 0 ? selectedScenarioIds : null,
        onMessage: (data) => {
          if (data.type === 'scenario_started' && data.message.scenarioTitle) {
            const title = data.message.scenarioTitle;
            const count = scenarioStartCountRef.current[title] ?? 0;
            const matching = scenarios.filter(s => s.scenarioTitle === title);
            currentScenarioIdRef.current = matching[count]?.scenarioID ?? null;
            scenarioStartCountRef.current[title] = count + 1;
            scenarioWarningsRef.current = 0;
          }
          // Split the engine's total failure count into errors vs warnings using
          // the severities recorded during this scenario (all arrive before this).
          if (data.type === 'scenario_finished_with_errors') {
            const total = data.message.vars?.scenarioErrors ?? 0;
            const warnings = Math.min(scenarioWarningsRef.current, total);
            const errors = Math.max(total - warnings, 0);
            const parts = [];
            if (errors > 0) parts.push(`${errors} error${errors !== 1 ? 's' : ''}`);
            if (warnings > 0) parts.push(`${warnings} warning${warnings !== 1 ? 's' : ''}`);
            const summary = parts.length ? parts.join(' and ') : `${total} issue${total !== 1 ? 's' : ''}`;
            data.message.text = `The "${data.message.vars?.scenarioTitle}" scenario finished with ${summary}.`;
          }
          if (data.message.text) addTestLog(data?.type, data.message.text, data.message.debugInfo ?? null);
          // Remember this step's severity so the matching step_error can be
          // classified as a warning (operational failure) or error (real DLG error).
          if (
            typeof data?.type === "string" &&
            data.type.startsWith("error_") &&
            data.message?.vars?.stepNumber != null
          ) {
            const sev = STEP_FAILURE_LOG_TYPES.has(data.type) ? "warning" : "error";
            stepSeverityRef.current[`${data.message.vars.scenarioTitle}::${data.message.vars.stepNumber}`] = sev;
            if (sev === "warning") scenarioWarningsRef.current += 1;
          }
        },
        onMessageStep: (data) => {
          const stepNumber = data?.message.stepNumber;
          const scenarioTitle = data?.message.scenarioTitle;
          const text = data?.message.text;
          let type = "info";
          if (data?.type === "step_error") {
            type = stepSeverityRef.current[`${scenarioTitle}::${stepNumber}`] ?? "error";
          }
          addTestLogStep(type, text, stepNumber, scenarioTitle, currentScenarioIdRef.current);
        },
        onScreencast: (blobUrl) => {
          setScreencastUrl(blobUrl)
          setScreenCastLoading(false)
          !screenCastLive && setScreenCastLive(true);
        },
        onOpen: () => addTestLog("system", "DataLayer Guard connected."),
        onError: () => { addTestLog("error", "DataLayer Guard error occurred."); setScreenCastLoading(false); },
        onClose: () => {
          setIsTestRunning(false);
          setScreenCastLive(false);
          setScreenCastLoading(false);
          if (wsRef.current !== null) {
            setOpenResults(true);
          }
        },
        finishTest: () => finishTest(),
        onProgress: (data) => {
          setTestProgress(data.message);
        },
        onResults: (data) => {
          const incoming = data?.message?.scenarios || {};
          // Tag each failed step with the severity recorded from the error_* stream,
          // so the results panel can show operational failures as warnings, not errors.
          const tagged = Object.fromEntries(
            Object.entries(incoming).map(([scenarioId, scenarioData]) => {
              const steps = scenarioData?.steps || {};
              const nextSteps = Object.fromEntries(
                Object.entries(steps).map(([stepNumber, step]) => {
                  const sev = stepSeverityRef.current[`${step.stepScenario}::${stepNumber}`];
                  return [stepNumber, sev ? { ...step, stepSeverity: sev } : step];
                }),
              );
              return [scenarioId, { ...scenarioData, steps: nextSteps }];
            }),
          );
          setTestScenarioResults(prev => ({
            ...prev,
            scenarios: {
              ...(prev?.scenarios || {}),
              ...tagged,
            },
          }));
        },
      });
      wsRef.current = ws;
    } catch (err) {
      addTestLog("error", `Failed to start the test. Please try again. If the issue persists, contact support@code-cube.io with the following error: ${err.message}`);
      setIsTestRunning(false);
    }
  };

  const stopTest = async () => {
    await datalayerguardService.stopDatalayerGuard();
    const ws = wsRef.current;
    wsRef.current = null;
    ws?.close();
    setIsTestRunning(false);
    setOpenResults(false);
    setScreencastUrl(null);
    setScreenCastLive(false);
    setScreenCastLoading(false);
    setTestProgress({ current: 0, total: 0, percentage: 0 });
  };
  const screenCastStatusText = () => {
    if (isTestRunning && screenCastLive) {
      return "Live"
    } else if(isTestRunning){
      return "Pending"
    } else{
      return "Waiting for the test"
    }
  }
  const handleScroll = (type) => {
    const element = (type === "steps") ? scrollRefSteps.current : scrollRefLogs.current;
    if (!element) {console.log('no element'); return };
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
    (type === "steps") ? setAutoScrollSteps(atBottom) : setAutoScrollLogs(atBottom);
  }
  useEffect(() => {
    const handleUnload = () => {
      if (wsRef.current) {
        datalayerguardService.stopDatalayerGuard();
        wsRef.current.close();
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      if (wsRef.current) {
        datalayerguardService.stopDatalayerGuard();
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (autoScrollLogs) {
      logsBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [testLogs])
  useEffect(() => {
    if (autoScrollSteps) {
      stepsBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [testLogsSteps])

  return (
    <>
    <Drawer
      className="test-scenario-drawer"
      rootClassName="test-scenario-drawer-root"
      placement="right"
      open={testModalOpen}
      title="Test scenario"
      onClose={() => {
        setTestModalOpen(false);
      }}
      width="90vw"
    >
      <div className="drawer-head">
        <div className="test-panel-screencast-head">
          {/* <div className="test-panel-screencast-subdiv"> */}
            <div className="test-panel-screencast-side">
              <div className="test-panel-screencast-side-buttons">
                <div className="test-console-actions">
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All scenarios"
                    disabled={isTestRunning}
                    value={selectedScenarioIds}
                    onChange={setSelectedScenarioIds}
                    style={{ minWidth: 220, maxWidth: 300, width: "100%" }}
                    options={scenarios.map((s) => ({
                      value: s.scenarioID,
                      label: s.scenarioTitle || "Untitled scenario",
                    }))}
                  />

                  <div className="test-console-actions-buttons">
                    <Button danger={isTestRunning} onClick={isTestRunning ? stopTest : startTest}>{isTestRunning ? "Stop" : "Start"}</Button>

                    {/* Currently disabled because there is no reason to have this button */}
                    {/* <Button onClick={() => setOpenResults(!openResults)}>{!openResults ? "Open results" : "Close results"}</Button> */}
                    
                    {/* Currently disabled because there is no reason to have this button */}
                    {/* <Button onClick={() => setShowScreencast(!showScreencast)}>Extract to window</Button> */}
                  </div>
                </div>
                  
              </div>
              <div className="test-stats-block">
                <div className={`test-status-pill test-status-pill--${isTestRunning && screenCastLive ? "live" : isTestRunning ? "pending" : "waiting"}`}>
                  {testProgress.total > 0 && (
                    <div className="test-status-progress-fill" style={{ width: `${testProgress.percentage}%` }} />
                  )}
                  <span className="test-status-dot" />
                  {screenCastStatusText()}
                  {testProgress.total > 0 && (
                    <span className="test-status-progress-pct">{testProgress.percentage}%</span>
                  )}
                </div>
                <div className="test-counters-grid">
                  <div className="test-counter-chip">
                    <span className="test-counter-num">{totalCollectedDatalayers}</span>
                    <span className="test-counter-lbl">Datalayers</span>
                  </div>
                  <div className={`test-counter-chip${totalCollectedErrors > 0 ? " test-counter-chip--err" : " test-counter-chip--ok"}`}>
                    <span className={`test-counter-num${totalCollectedErrors > 0 ? " test-counter-num--err" : " test-counter-num--ok"}`}>{totalCollectedErrors}</span>
                    <span className="test-counter-lbl">Errors</span>
                  </div>
                  <div className="test-counter-chip">
                    <span className="test-counter-num">{totalCollectedScenarios}</span>
                    <span className="test-counter-lbl">Scenarios</span>
                  </div>
                </div>
                <div className={`test-legend${legendOpen ? " test-legend--open" : ""}`}>
                  <button
                    type="button"
                    className="test-legend-toggle"
                    onClick={() => setLegendOpen((prev) => !prev)}
                    aria-expanded={legendOpen}
                  >
                    
                    <span className="test-legend-toggle-lbl">Legend</span>
                    <span className={`test-legend-arrow${legendOpen ? " test-legend-arrow--open" : ""}`}>&#9654;</span>
                  </button>
                  {legendOpen && (
                    <div className="test-legend-body">
                      <span className="test-legend-item">
                        <span className="test-legend-dot test-legend-dot--success" />
                        <span className="test-legend-text">
                          <span className="test-legend-label">Success</span>
                          <span className="test-legend-desc">Step completed as expected</span>
                        </span>
                      </span>
                      <span className="test-legend-item">
                        <span className="test-legend-dot test-legend-dot--warning" />
                        <span className="test-legend-text">
                          <span className="test-legend-label">Warning</span>
                          <span className="test-legend-desc">Step has failed. This could cause the DataLayer Guard to not reach your dataLayer or event</span>
                        </span>
                      </span>
                      <span className="test-legend-item">
                        <span className="test-legend-dot test-legend-dot--error" />
                        <span className="test-legend-text">
                          <span className="test-legend-label">Error</span>
                          <span className="test-legend-desc">DataLayer Guard couldn't find the expected dataLayer or event, or the parameters didn't match your template.</span>
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!openResults ? (
              <div className="test-panel-image-head">
                {screencastUrl ? (
                  <img src={screencastUrl} className="test-panel-image" alt="" />
                ) : screenCastLoading ? (
                  <div className="screencast-loading">
                    <div className="screencast-loading-spinner" />
                    <p className="screencast-loading-label">Loading live preview...</p>
                  </div>
                ) : (
                  <div className="screencast-placeholder">
                    <p className="screencast-placeholder-label">
                      {isTestRunning ? (
                        "Waiting for browser feed…"
                      ) : (
                        <>
                          <button
                            type="button"
                            className="screen-start-button"
                            onClick={() => startTest()}
                          >
                            Start
                          </button>{" "}
                          a test to see the live browser feed
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="test-panel-image-head">
                <TestScenarioWindowResultsPanel results={testScenarioResults} />
              </div>
            )}
          {/* </div> */}
        </div>
        <div className="test-console-panel">
          <div className="test-console-output">
            <div className="test-console-steps">
            <div className="test-console-section-header">
              <p>
                Live steps from the DataLayer Guard
                <Tooltip title="Below you can see real-time steps of your DataLayer Guard test takes. If you have any questions about the logs or how to interpret them, please visit our documentation page.">
                  <InfoCircleFilled
                    size={15}
                    className="info-icon"
                    style={{ marginLeft: "10px" }}
                  />
                </Tooltip>
              </p>
            </div>
            <div className="test-console-scroll-body" ref={scrollRefSteps} onScroll={() => handleScroll('steps')}>
              {testLogsSteps.length === 0 ? (
                <span className="test-console-empty">
                  {testLogsSteps.length === 0 ? "Wait for the DLG to start" : ""}
                </span>
              ) : (
                testLogsSteps.map((log, i) => {
                  const prevLog = testLogsSteps[i - 1];
                  const isNewScenario = !prevLog || (
                    log.scenarioId != null
                      ? prevLog.scenarioId !== log.scenarioId
                      : prevLog.scenarioTitle !== log.scenarioTitle
                  );
                  return (
                    <React.Fragment key={i}>
                      {isNewScenario && log.scenarioTitle && (
                        <div className="step-scenario-header">
                          <span className="step-scenario-title">{log.scenarioTitle}:</span>
                        </div>
                      )}
                      <div className={`test-console-line test-console-${log.type}`}>
                        {log.stepNumber && log.scenarioTitle && onOpenScenario ? (
                          <>
                            {log.type === 'error'
                              ? <CloseOutlined className="step-status-icon step-status-icon--failed" />
                              : log.type === 'warning'
                                ? <ExclamationCircleOutlined className="step-status-icon step-status-icon--warning" />
                                : <CheckOutlined className="step-status-icon step-status-icon--success" />
                            }
                            {" "}
                            <button
                              className="step-log-link"
                              onClick={() => onOpenScenario(log.scenarioTitle, log.stepNumber, log.scenarioId)}
                            >
                              Step {log.stepNumber}
                            </button>
                          </>
                        ) : (
                          typeof log.text === 'object' ? JSON.stringify(log.text) : log.text
                        )}
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              <div ref={stepsBottomRef} />
            </div>
          </div>
            <div className="test-console-logs">
              <div className="test-console-section-header">
                <p>
                  Live logs from the DataLayer Guard
                  <Tooltip title="Below you can see real-time logs of your DataLayer Guard test. If you have any questions about the logs or how to interpret them, please visit our documentation page.">
                    <InfoCircleFilled
                      size={15}
                      className="info-icon"
                      style={{ marginLeft: "10px" }}
                    />
                  </Tooltip>
                </p>
              </div>
              <div className="test-console-scroll-body" ref={scrollRefLogs} onScroll={() => handleScroll('logs')}>
                {testLogs.length === 0 ? (
                  <span className="test-console-empty">
                    Press Start to begin...
                  </span>
                ) : (
                  testLogs.map((log, i) => (
                    <div
                      key={i}
                      className={`test-console-line test-console-${log.baseType}`}
                    >
                      <span className="test-console-time">[{log.time}]</span>{" "}
                      <span>
                        {typeof log.text === 'object' ? JSON.stringify(log.text) : log.text}
                        {log.debugInfo && (
                          <Tooltip title={log.debugInfo}>
                            <InfoCircleFilled
                              size={15}
                              className="info-icon"
                              style={{ marginLeft: "10px" }}
                            />
                          </Tooltip>
                        )}
                      </span>
                    </div>
                  ))
                )}
                <div ref={logsBottomRef} />
              </div>
            </div>
        </div> 
      </div>
      </div>
    </Drawer>
    {showScreencast && (
      <ScreencastWindow
        src={screencastUrl}
        onClose={() => setShowScreencast(!showScreencast)}
      />
    )}
    </>
  )
}
const ScenariosMenu = () => {
  const {
    companyId = "",
    configParams = {},
    setConfigParams = () => {},
    pageScenariosDescLink,
    scenariosList = [],
    reusableComponents = [],
    drawerGlobalState = false,
    setDrawerGlobalState = () => {},
    saveFunction = () => {},
    defaultEventTemplates = [],
    simplifyDLGEventJson = () => {},
    transformDataObject = () => {},
    error = () => {},
    success = () => {},
    peekScenarioId = null,
    clearPeekScenarioId = () => {},
    peekComponent = () => {},
    peekComponentId = null,
  } = useContext(ScenariosContext);

  // Constant variables
  const defaultPlacement = "right";
  const secondDrawerTitle = "Scenario Steps List";
  const thirdDrawerTitle = "DataLayer Template";
  const cookiesDrawerTitle = "Cookies Scenario Steps List";
  const firstDrawerWidth = "75%";
  const secondDrawerWidth = "75%";
  const thirdDrawerWidth = "75%";

  const defaultMatchType = scenarioStartPageMatchTypes[0]?.key;
  const defaultPageType = scenarioStartPageTypes[0]?.key;

  const [jsonBtnText, setJsonBtnText] = useState("add json");
  const [jsonBtnIcon, setJsonBtnIcon] = useState(<PlusOutlined />);

  // Template modal content code for animation and width control
  const [templateModalContentCode, setTemplateModalContentCode] =
    useState("main");
  const [thirdDrawerClassName, setThirdDrawerClassName] = useState("");
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testScenarioId, setTestScenarioId] = useState(null);
  const [scrollToStep, setScrollToStep] = useState(null);

  // Drawers control area
  const [secondDrawerVisible, setSecondDrawerVisible] = useState(false);
  const [thirdDrawerVisible, setThirdDrawerVisible] = useState(false);
  const [cookiesDrawerVisible, setCookiesDrawerVisible] = useState(false);
  const [loginDrawerVisible, setLoginDrawerVisible] = useState(false);

  // Unsaved changes tracking
  const [scenarioSaveStatus, setScenarioSaveStatus] = useState(false);
  const [isScenarioSaving, setIsScenarioSaving] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const initialScenarioRef = useRef(null);

  // Per-sub-drawer unsaved-change tracking. Cookies and login each get their
  // own snapshot + modal so closing them with unsaved edits offers the same
  // discard / save-changes choice as the parent scenario drawer.
  const [showCookiesUnsavedModal, setShowCookiesUnsavedModal] = useState(false);
  const [showLoginUnsavedModal, setShowLoginUnsavedModal] = useState(false);
  const initialCookiesRef = useRef(null);
  const initialLoginRef = useRef(null);

  // Skip flags for the reset useEffects on `selectedScenario` / `scenarioCookies`
  // / `scenarioLogin` changes. When we pop back via the linked-scenario stack
  // we restore in-progress state from the snapshot directly — these flags
  // prevent the cascading resets from overwriting the restored values.
  const skipResetStateRef = useRef(false);
  const skipCookiesCascadeRef = useRef(false);
  const skipLoginCascadeRef = useRef(false);
  const skipInitialRefResetRef = useRef(false);

  const showSecondDrawer = () => setSecondDrawerVisible(true);

  // Back-stack of scenarioIDs the user navigated through via `scenario_link`
  // blocks. Closing the 2nd drawer pops back to the previous entry instead of
  // exiting all the way out, so live-link navigation doesn't lose context.
  const [linkedScenarioStack, setLinkedScenarioStack] = useState([]);

  const closeSecondDrawer = () => {
    if (linkedScenarioStack.length > 0) {
      const previousEntry = linkedScenarioStack[linkedScenarioStack.length - 1];
      const prevScenario = curScenariosList?.find(
        (s) => s?.scenarioID === previousEntry?.scenarioID,
      );
      setLinkedScenarioStack((prev) => prev.slice(0, -1));
      if (prevScenario && previousEntry?.snapshot) {
        const snap = previousEntry.snapshot;
        // Suppress every reset useEffect about to fire so the snapshot
        // values aren't immediately clobbered. Each flag clears itself the
        // next time its effect runs.
        skipResetStateRef.current = true;
        skipInitialRefResetRef.current = true;
        skipCookiesCascadeRef.current = true;
        skipLoginCascadeRef.current = true;
        // Restore the dirty-tracking baseline FIRST so the change-tracker
        // useEffect (which we don't skip) compares restored state against
        // the original-open snapshot, not the just-popped scenario's one.
        initialScenarioRef.current = snap.initialScenarioRef;
        setSelectedScenario(prevScenario);
        setScenarioTitle(snap.scenarioTitle);
        setScenarioStatus(snap.scenarioStatus);
        setScenarioStartPage(snap.scenarioStartPage);
        setScenarioStartPageMatchType(snap.scenarioStartPageMatchType);
        setScenarioStartPageType(snap.scenarioStartPageType);
        setScenarioSteps(snap.scenarioSteps);
        setScenarioCookies(snap.scenarioCookies);
        setScenarioLogin(snap.scenarioLogin);
        setScenarioCookiesStatus(snap.scenarioCookiesStatus);
        setScenarioCookiesSteps(snap.scenarioCookiesSteps);
        setScenarioLoginStatus(snap.scenarioLoginStatus);
        setScenarioLoginSteps(snap.scenarioLoginSteps);
        setUrlTldValid(snap.urlTldValid);
        setUrlTldError(snap.urlTldError);
        setSelectedTemplateStr("");
        return;
      }
      // Previous scenario no longer exists (deleted) — fall through and close.
    }
    setSecondDrawerVisible(false);
  };

  const showCookiesDrawer = () => setCookiesDrawerVisible(true);
  const closeCookiesDrawer = () => setCookiesDrawerVisible(false);

  const showLoginDrawer = () => setLoginDrawerVisible(true);
  const closeLoginDrawer = () => setLoginDrawerVisible(false);

  const showThirdDrawer = (item, stepId, stepType = "") => {
    const parametersList = templateElemsList(item);
    setSelectedTemplate(parametersList);
    setSelectedTemplateStr(JSON.stringify(parametersList) || "");
    setSelectedStepId(stepId);
    setSelectedStepType(stepType);
    setTemplateModalContentCode("main");
    try {
      initialTemplateRef.current = JSON.parse(JSON.stringify(parametersList));
    } catch (err) {
      initialTemplateRef.current = parametersList;
    }
    setTemplateSaveStatus(false);
    setThirdDrawerVisible(true);
  };
  const closeThirdDrawer = () => {
    setThirdDrawerVisible(false);
    setTemplateModalContentCode("main");
    setShowTemplateUnsavedModal(false);
    setTemplateSaveStatus(false);
  };

  // Adjust DataLayer template drawer width based on content code
  useEffect(() => {
    const widthClass = templateModalContentCode === "edit" ? "modal-wide" : "";
    setThirdDrawerClassName(widthClass);
  }, [templateModalContentCode, thirdDrawerVisible]);

  // Scenarios list modal
  const [curScenariosList, setCurScenariosList] = useState(null);
  const [curScenariosEvents, setCurScenariosEvents] = useState({});
  const [pendingComponents, setPendingComponents] = useState([]);

  useEffect(() => {
    setCurScenariosList(scenariosList);
    const scenariosDropdownList = scenariosList.map((elem, idx) => ({
      key: elem?.scenarioID || idx,
      label: elem?.scenarioTitle || `Untitled Scenario ${idx + 1}`,
    }));

    setStepsScenarioOptions(
      getScenariosListProps({
        listUpdateFunction: setScenarioSteps,
        scenariosDropdownList: scenariosDropdownList,
        stepsType: "steps",
        modalCancelFunction: handleStepsListModalCancel,
        modalStateChangeFunction: setStepsListChangeModalState,
      }),
    );

    setCookiesStepsScenarioOptions(
      getScenariosListProps({
        listUpdateFunction: setScenarioCookiesSteps,
        scenariosDropdownList: scenariosDropdownList,
        stepsType: "cookies",
        modalCancelFunction: handleCookiesStepsListModalCancel,
        modalStateChangeFunction: setCookiesStepsListChangeModalState,
      }),
    );

    setLoginStepsScenarioOptions(
      getScenariosListProps({
        listUpdateFunction: setScenarioLoginSteps,
        scenariosDropdownList: scenariosDropdownList,
        stepsType: "login",
        modalCancelFunction: handleLoginStepsListModalCancel,
        modalStateChangeFunction: setLoginStepsListChangeModalState,
      }),
    );

    const scenariosEvents = {};

    scenariosList.forEach((elem) => {
      const steps = Array.isArray(elem?.steps)
        ? elem.steps
        : Object.values(elem?.steps || {});
      const events = collectMonitoredEventSteps(
        steps,
        (componentId) => {
          const component =
            reusableComponents?.find((item) => item.componentId === componentId) ||
            pendingComponents.find((item) => item.componentId === componentId);
          return component?.steps || [];
        },
      ).filter((step) =>
        ["collect_dl_object", "collect_dl_before_navigation"].includes(
          step?.type ?? step?.stepType,
        ),
      );

      scenariosEvents[elem.scenarioID] =
        events?.map((event) => ({
          stepNum: event?.step,
          eventName: getMonitoredEventName(event),
        })) || [];
    });

    setCurScenariosEvents(scenariosEvents);
  }, [scenariosList, reusableComponents, pendingComponents]);

  const [pauseModalVisible, setPauseModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [targetScenarioId, setTargetScenarioId] = useState(null);

  // Called when user clicks the trash icon in the table
  const confirmDeleteScenario = (id) => {
    setTargetScenarioId(id);
    setDeleteModalVisible(true);
  };

  // Called when they click “OK” in the delete scenario popup
  const handleDeleteConfirmed = () => {
    if (!targetScenarioId) return;
    flushSync(() => {
      setDeleteModalVisible(false);
      setTargetScenarioId(null);
      scenarioRemove(targetScenarioId);
    });
  };

  // Delete Scenario logic
  const scenarioRemove = (id) => {
    // Find the scenario being deleted so we can snapshot its steps for detaching
    const deletedScenario = curScenariosList.find((s) => s.scenarioID === id);

    // Track which scenarios had live links auto-detached so we can report it.
    const detachedScenarioIds = new Set();

    const detachLinksToScenario = (steps, scenarioId) => {
      if (!Array.isArray(steps)) return steps;
      const result = [];
      steps.forEach((item) => {
        if (
          item?.blockType === "scenario_link" &&
          item?.sourceScenarioId === id
        ) {
          detachedScenarioIds.add(scenarioId);
          // Resolve and clone the linked steps before the scenario is deleted
          let sourceSteps = [];
          if (deletedScenario) {
            switch (item.sourceStepsType) {
              case "steps":
                sourceSteps = deletedScenario.steps?.filter((s) => !s.blockType) || [];
                break;
              case "cookies":
                sourceSteps = deletedScenario.cookiesAcceptance?.steps?.filter((s) => !s.blockType) || [];
                break;
              case "login":
                sourceSteps = deletedScenario.loginInfo?.steps?.filter((s) => !s.blockType) || [];
                break;
              default:
                console.warn(
                  `scenarioRemove: unknown sourceStepsType "${item.sourceStepsType}"`,
                );
            }
          }
          sourceSteps.forEach((step) => {
            result.push({ ...step, stepId: generateUniqueId() });
          });
        } else {
          result.push(item);
        }
      });
      return result;
    };

    // a) update other scenarios that have live links to the deleted one
    const updatedScenarios = curScenariosList
      .filter((s) => s.scenarioID !== id)
      .map((scenario) => ({
        ...scenario,
        steps: detachLinksToScenario(scenario.steps, scenario.scenarioID),
        cookiesAcceptance: {
          ...scenario.cookiesAcceptance,
          steps: detachLinksToScenario(
            scenario.cookiesAcceptance?.steps,
            scenario.scenarioID,
          ),
        },
        loginInfo: {
          ...scenario.loginInfo,
          steps: detachLinksToScenario(
            scenario.loginInfo?.steps,
            scenario.scenarioID,
          ),
        },
      }));

    // b) update local state
    setCurScenariosList(updatedScenarios);

    // c) close the detail drawer & any confirmation modal
    setScenarioRemoveConfirmation(false);
    closeSecondDrawer();

    // d) push the change back up into your configParams + trigger save
    const updated = { ...configParams, scenarios: updatedScenarios };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);

    const detachedCount = detachedScenarioIds.size;
    if (detachedCount > 0) {
      success(
        `Scenario deleted. Auto-detached live links in ${detachedCount} other scenario${detachedCount !== 1 ? "s" : ""}.`,
      );
    }
  };

  // Called when user clicks the pouse icon in the table
  const confirmPauseScenario = (id) => {
    setTargetScenarioId(id);
    setPauseModalVisible(true);
  };

  // Called when they click “OK” in the pause scenario popup
  const handlePauseConfirmed = () => {
    if (!targetScenarioId) return;
    flushSync(() => {
      setPauseModalVisible(false);
      setTargetScenarioId(null);
      scenarioPause(targetScenarioId);
    });
  };

  // Pause Scenario logic
  const scenarioPause = (id) => {
    // 1) build the new list with active=false for that one ID
    const newList = curScenariosList.map((s) =>
      s.scenarioID === id ? { ...s, active: false } : s,
    );

    // 2) update the table
    setCurScenariosList(newList);

    // 3) push it back into your in-memory config and flag for save
    const updated = { ...configParams, scenarios: newList };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);

    // 4) close the detail drawer
    closeSecondDrawer();
  };

  // Activate Scenario logic
  const activateScenario = (id) => {
    // 1) build the new list with active=false for that one ID
    const newList = curScenariosList.map((s) =>
      s.scenarioID === id ? { ...s, active: true } : s,
    );

    // 2) update the table
    setCurScenariosList(newList);

    // 3) push it back into your in-memory config and flag for save
    const updated = { ...configParams, scenarios: newList };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);

    // 4) close the detail drawer
    closeSecondDrawer();
  };

  const headerStyle = {
    background: "#fff",
    backgroundImage: "none",
    boxShadow: "none",
    filter: "none",
  };

  const scenarioColumns = [
    {
      title: "Scenario name",
      dataIndex: "scenarioTitle",
      key: "scenarioTitle",
      align: "left",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Monitored events",
      dataIndex: "left",
      key: "events",
      align: "left",
      render: (_, record) => (
        <span style={{ fontStyle: "italic" }}>
          {curScenariosEvents[record?.scenarioID]
            ?.map((event) => event?.eventName)
            .filter(Boolean)
            .join(", ") || "-"}
        </span>
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "left",
      align: "middle",
      render: (active) => (active ? "active" : "paused"),
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space size={14}>
          <DeleteOutlined
            style={{ color: "#000", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              confirmDeleteScenario(record?.scenarioID);
            }}
          />
          {record.active ? (
            // if it’s active, show Pause
            <PauseCircleOutlined
              style={{ color: "#000", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                confirmPauseScenario(record.scenarioID);
              }}
            />
          ) : (
            // if it’s paused, show Play
            <PlayCircleOutlined
              style={{ color: "#000", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                activateScenario(record.scenarioID);
              }}
            />
          )}

          <EditOutlined
            style={{ color: "#000", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleEditScenarioClick(record?.scenarioID);
            }}
          />
        </Space>
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    },
  ];

  // Scenario edit modal
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarioTitle, setScenarioTitle] = useState("");
  const [scenarioId, setScenarioId] = useState(null);
  const [scenarioStatus, setScenarioStatus] = useState(false);
  const [scenarioStartPage, setScenarioStartPage] = useState(false);
  const [scenarioStartPageMatchType, setScenarioStartPageMatchType] =
    useState(null);
  const [scenarioStartPageType, setScenarioStartPageType] = useState(null);
  const [scenarioSteps, setScenarioSteps] = useState([]);

  // TLD validation state
  const [urlTldValid, setUrlTldValid] = useState(true);
  const [urlTldError, setUrlTldError] = useState("");

  const [scenarioCookies, setScenarioCookies] = useState(null);
  const [scenarioCookiesStatus, setScenarioCookiesStatus] = useState(false);
  const [scenarioCookiesSteps, setScenarioCookiesSteps] = useState(false);

  const [scenarioLogin, setScenarioLogin] = useState(null);
  const [scenarioLoginStatus, setScenarioLoginStatus] = useState(false);
  const [scenarioLoginSteps, setScenarioLoginSteps] = useState(false);

  const [scenarioEvents, setScenarioEvents] = useState([]);

  // Updating data with selecting a new scenario from the list
  useEffect(() => {
    if (skipResetStateRef.current) {
      skipResetStateRef.current = false;
      return;
    }
    setScenarioTitle(selectedScenario?.scenarioTitle || "");
    setScenarioId(selectedScenario?.scenarioID || null);
    setScenarioStatus(!!selectedScenario?.active);
    setScenarioStartPage(selectedScenario?.urlStartingPoint || "");
    setScenarioStartPageMatchType(
      selectedScenario?.urlStartingPointMatchType || defaultMatchType,
    );
    setScenarioStartPageType(
      selectedScenario?.urlStartingPointPageType || defaultPageType,
    );
    setScenarioSteps(ensureUniqueBlockIds(selectedScenario?.steps || []));
    setScenarioCookies(selectedScenario?.cookiesAcceptance || {});
    setScenarioLogin(selectedScenario?.loginInfo || {});
    setScenarioSteps(_.cloneDeep(selectedScenario?.steps || []));
    setScenarioCookies(_.cloneDeep(selectedScenario?.cookiesAcceptance || {}));
    setScenarioLogin(_.cloneDeep(selectedScenario?.loginInfo || {}));
    setScenarioEvents(curScenariosEvents[selectedScenario?.scenarioID]);

    // Validate TLD when scenario loads
    const startPage = selectedScenario?.urlStartingPoint || "";
    if (startPage && startPage.trim() !== "") {
      const isValid = validateDomainTLD(startPage);
      setUrlTldValid(isValid);
      setUrlTldError(isValid ? "" : getTLDErrorMessage(startPage));
    } else {
      setUrlTldValid(true);
      setUrlTldError("");
    }
  }, [selectedScenario]);

  useEffect(() => {
    if (skipCookiesCascadeRef.current) {
      skipCookiesCascadeRef.current = false;
      return;
    }
    setScenarioCookiesStatus(!!scenarioCookies?.active);
    // Deep-clone the steps so SortableItem's in-place mutations on the
    // editor copy don't bleed into the underlying scenario state — the
    // dirty-check below would otherwise see snapshot and current as
    // identical because they share the same step object references.
    const baseCookies = ensureUniqueBlockIds(scenarioCookies?.steps || []);
    setScenarioCookiesSteps(JSON.parse(JSON.stringify(baseCookies)));
  }, [scenarioCookies]);

  useEffect(() => {
    if (skipLoginCascadeRef.current) {
      skipLoginCascadeRef.current = false;
      return;
    }
    setScenarioLoginStatus(!!scenarioLogin?.required);
    const baseLogin = ensureUniqueBlockIds(scenarioLogin?.steps || []);
    setScenarioLoginSteps(JSON.parse(JSON.stringify(baseLogin)));
  }, [scenarioLogin]);

  // Snapshot the cookies / login state when their drawers open, so we can
  // detect dirty edits and offer save / discard on close. Re-snapshot every
  // time the drawer reopens.
  useEffect(() => {
    if (cookiesDrawerVisible) {
      initialCookiesRef.current = {
        status: scenarioCookiesStatus,
        steps: JSON.parse(
          JSON.stringify(
            Array.isArray(scenarioCookiesSteps) ? scenarioCookiesSteps : [],
          ),
        ),
      };
    }
  }, [cookiesDrawerVisible]);

  useEffect(() => {
    if (loginDrawerVisible) {
      initialLoginRef.current = {
        status: scenarioLoginStatus,
        steps: JSON.parse(
          JSON.stringify(
            Array.isArray(scenarioLoginSteps) ? scenarioLoginSteps : [],
          ),
        ),
      };
    }
  }, [loginDrawerVisible]);

  const cookiesDirty = (() => {
    if (!cookiesDrawerVisible || !initialCookiesRef.current) return false;
    const currentSteps = Array.isArray(scenarioCookiesSteps)
      ? scenarioCookiesSteps
      : [];
    return (
      initialCookiesRef.current.status !== scenarioCookiesStatus ||
      JSON.stringify(initialCookiesRef.current.steps) !==
        JSON.stringify(currentSteps)
    );
  })();

  const loginDirty = (() => {
    if (!loginDrawerVisible || !initialLoginRef.current) return false;
    const currentSteps = Array.isArray(scenarioLoginSteps)
      ? scenarioLoginSteps
      : [];
    return (
      initialLoginRef.current.status !== scenarioLoginStatus ||
      JSON.stringify(initialLoginRef.current.steps) !==
        JSON.stringify(currentSteps)
    );
  })();

  const discardAndCloseCookies = () => {
    if (initialCookiesRef.current) {
      setScenarioCookiesStatus(initialCookiesRef.current.status);
      setScenarioCookiesSteps(
        JSON.parse(JSON.stringify(initialCookiesRef.current.steps)),
      );
    }
    setShowCookiesUnsavedModal(false);
    closeCookiesDrawer();
  };

  const discardAndCloseLogin = () => {
    if (initialLoginRef.current) {
      setScenarioLoginStatus(initialLoginRef.current.status);
      setScenarioLoginSteps(
        JSON.parse(JSON.stringify(initialLoginRef.current.steps)),
      );
    }
    setShowLoginUnsavedModal(false);
    closeLoginDrawer();
  };

  // Scroll to step in scenario drawer after it opens
  useEffect(() => {
    if (secondDrawerVisible && scrollToStep !== null) {
      const timeoutId = setTimeout(() => {
        const badges = document.querySelectorAll(".scenario-step-number");
        const target = Array.from(badges).find(
          (el) => el.textContent.trim() === String(scrollToStep)
        );
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        setScrollToStep(null);
      }, 350);
      return () => clearTimeout(timeoutId);
    }
  }, [secondDrawerVisible, scrollToStep]);

  // Store initial scenario state when scenario drawer opens
  useEffect(() => {
    if (skipInitialRefResetRef.current) {
      skipInitialRefResetRef.current = false;
      return;
    }
    if (selectedScenario && secondDrawerVisible) {
      initialScenarioRef.current = {
        title: selectedScenario?.scenarioTitle || "",
        startPage: selectedScenario?.urlStartingPoint || "",
        matchType:
          selectedScenario?.urlStartingPointMatchType || defaultMatchType,
        pageType: selectedScenario?.urlStartingPointPageType || defaultPageType,
        steps: selectedScenario?.steps || [],
        // Sourced from selectedScenario (not from scenarioCookiesStatus /
        // scenarioLoginStatus) because those are set async via separate
        // useEffects and could still hold the previous scenario's value at
        // the moment this snapshot fires.
        cookiesStatus: !!selectedScenario?.cookiesAcceptance?.active,
        loginStatus: !!selectedScenario?.loginInfo?.required,
      };
      setScenarioSaveStatus(false);
    }
  }, [selectedScenario?.scenarioID, secondDrawerVisible]);

  // Track changes to scenario fields
  useEffect(() => {
    if (!initialScenarioRef.current || !secondDrawerVisible) return;

    const currentState = {
      title: scenarioTitle,
      startPage: scenarioStartPage,
      matchType: scenarioStartPageMatchType,
      pageType: scenarioStartPageType,
      steps: scenarioSteps,
      cookiesStatus: scenarioCookiesStatus,
      loginStatus: scenarioLoginStatus,
    };

    const hasChanges = !_.isEqual(initialScenarioRef.current, currentState);
    setScenarioSaveStatus(hasChanges);
  }, [
    scenarioTitle,
    scenarioStartPage,
    scenarioStartPageMatchType,
    scenarioStartPageType,
    scenarioSteps,
    scenarioCookiesStatus,
    scenarioLoginStatus,
    secondDrawerVisible,
  ]);

  const currentScenarioDraft = {
    title: scenarioTitle,
    startPage: scenarioStartPage,
    matchType: scenarioStartPageMatchType,
    pageType: scenarioStartPageType,
    steps: scenarioSteps,
    cookiesStatus: scenarioCookiesStatus,
    loginStatus: scenarioLoginStatus,
  };

  const hasUnsavedScenarioChanges = initialScenarioRef.current
    ? !_.isEqual(initialScenarioRef.current, currentScenarioDraft)
    : false;

  const resetScenarioDraftToLastSaved = () => {
    if (!initialScenarioRef.current) return;

    setScenarioTitle(initialScenarioRef.current.title);
    setScenarioStartPage(initialScenarioRef.current.startPage);
    setScenarioStartPageMatchType(initialScenarioRef.current.matchType);
    setScenarioStartPageType(initialScenarioRef.current.pageType);
    setScenarioSteps(_.cloneDeep(initialScenarioRef.current.steps));
    setScenarioCookiesStatus(initialScenarioRef.current.cookiesStatus);
    setScenarioLoginStatus(initialScenarioRef.current.loginStatus);

    // Re-validate TLD with restored value.
    const isValid = validateDomainTLD(initialScenarioRef.current.startPage);
    setUrlTldValid(isValid);
    setUrlTldError(
      isValid ? "" : getTLDErrorMessage(initialScenarioRef.current.startPage),
    );

    setScenarioSaveStatus(false);
  };

  // Handle starting point URL change with TLD validation
  const handleStartingPointUrlChange = (e) => {
    const value = e.target.value;
    setScenarioStartPage(value);

    // Validate TLD if value is not empty
    if (value && value.trim() !== "") {
      const isValid = validateDomainTLD(value);
      setUrlTldValid(isValid);
      if (!isValid) {
        setUrlTldError(getTLDErrorMessage(value));
      } else {
        setUrlTldError("");
      }
    } else {
      // Empty value is considered valid (optional field)
      setUrlTldValid(true);
      setUrlTldError("");
    }
  };

  const menuMatchTypes = {
    items: scenarioStartPageMatchTypes.map(({ key, label }) => ({
      key,
      label,
    })),
    onClick: ({ key }) => {
      setScenarioStartPageMatchType(key);
    },
  };

  const menuPageTypes = {
    items: scenarioStartPageTypes.map(({ key, label }) => ({ key, label })),
    onClick: ({ key }) => {
      setScenarioStartPageType(key);
    },
  };

  // Scenarios list editing area
  const handleEditScenarioClick = (id, { keepStack = false } = {}) => {
    const scenarioObj = curScenariosList.find((elem) => elem.scenarioID === id);
    setSelectedScenario(scenarioObj);
    setSelectedTemplateStr("");
    // Opening from the scenarios table starts a fresh navigation — wipe any
    // leftover back-stack. Live-link navigation passes keepStack to preserve it.
    if (!keepStack) setLinkedScenarioStack([]);
    showSecondDrawer();
  };

  // Peek mode: when a sibling menu (e.g. components) wants to show a scenario
  // edit drawer in-place over its own drawer, it sets `peekScenarioId`.
  // We open the inner edit drawer directly — independent of the outer
  // scenarios drawer, since antd portals each Drawer to <body>. Closing the
  // inner drawer returns the user to wherever they were.
  useEffect(() => {
    if (!peekScenarioId) return;
    if (!Array.isArray(curScenariosList) || curScenariosList.length === 0)
      return;
    const scenarioObj = curScenariosList.find(
      (s) => s?.scenarioID === peekScenarioId,
    );
    if (!scenarioObj) {
      clearPeekScenarioId();
      return;
    }
    setSelectedScenario(scenarioObj);
    setSelectedTemplateStr("");
    // Peek from ComponentsMenu starts a fresh navigation context — drop any
    // stale back-stack from prior scenario_link traversals.
    setLinkedScenarioStack([]);
    setSecondDrawerVisible(true);
    clearPeekScenarioId();
  }, [peekScenarioId, curScenariosList]);
  const testScenario = () => {
    setDrawerGlobalState(false);
    closeSecondDrawer();
    closeLoginDrawer();
    setTestScenarioId(null);
    setTestModalOpen(true);
  };

  const testSingleScenario = (scenarioID) => {
    setDrawerGlobalState(false);
    closeSecondDrawer();
    closeLoginDrawer();
    setTestScenarioId(scenarioID);
    setTestModalOpen(true);
  };

  const handleOpenScenarioAtStep = (scenarioTitle, stepNumber, scenarioId = null) => {
    const scenario = scenarioId
      ? curScenariosList?.find((s) => s.scenarioID === scenarioId)
      : curScenariosList?.find((s) => s.scenarioTitle === scenarioTitle);
    setDrawerGlobalState(true);
    closeLoginDrawer();
    if (scenario) {
      handleEditScenarioClick(scenario.scenarioID);
    }
    setScrollToStep(stepNumber);
  };

  const handleAddScenarioClick = () => {
    const newScenario = defaultScenarioObject();
    setSelectedScenario(newScenario);
    setCurScenariosList([...curScenariosList, newScenario]);
    setSelectedTemplateStr("");
    showSecondDrawer();
  };

  const handleManageConsentSettings = () => {
    // TODO
    // const newScenario = defaultScenarioObject();
    // setSelectedScenario(newScenario);
    // setCurScenariosList([...curScenariosList, newScenario]);
    // setSelectedTemplateStr("");
    // showSecondDrawer();
  };

  const [scenarioRemoveConfirmation, setScenarioRemoveConfirmation] =
    useState(false);

  // Steps list editing area
  const [stepsScenarioOptions, setStepsScenarioOptions] = useState({});
  const [cookiesStepsScenarioOptions, setCookiesStepsScenarioOptions] =
    useState({});
  const [loginStepsScenarioOptions, setLoginStepsScenarioOptions] = useState(
    {},
  );

  const [stepsListChangeModalState, setStepsListChangeModalState] =
    useState(false);
  const [
    cookiesStepsListChangeModalState,
    setCookiesStepsListChangeModalState,
  ] = useState(false);
  const [loginStepsListChangeModalState, setLoginStepsListChangeModalState] =
    useState(false);

  const handleStepsListModalCancel = () =>
    handleStepsListModalChangeState({
      newState: false,
      modalStateChangeFunction: setStepsListChangeModalState,
    });
  const handleCookiesStepsListModalCancel = () =>
    handleStepsListModalChangeState({
      newState: false,
      modalStateChangeFunction: setCookiesStepsListChangeModalState,
    });
  const handleLoginStepsListModalCancel = () =>
    handleStepsListModalChangeState({
      newState: false,
      modalStateChangeFunction: setLoginStepsListChangeModalState,
    });

  const [stepsListChangeModalOkFunction, setStepsListChangeModalOkFunction] =
    useState(() => {});

  const handleStepsListModalChangeState = (props) => {
    const {
      newState = false,
      okFunction = () => {},
      modalStateChangeFunction = () => {},
    } = props;

    modalStateChangeFunction(newState);
    setStepsListChangeModalOkFunction(() => okFunction);
  };

  const getScenariosListProps = (props) => {
    const {
      listUpdateFunction = () => {},
      scenariosDropdownList = [],
      stepsType = "",
      modalCancelFunction = () => {},
      modalStateChangeFunction = () => {},
    } = props;

    return {
      items: scenariosDropdownList,
      onClick: (e) =>
        handleStepsListModalChangeState({
          newState: true,
          okFunction: () =>
            copyStepsListFromAnotherScenario({
              scenarioId: e?.key,
              listUpdateFunction,
              stepsType,
              modalCancelFunction,
            }),
          modalStateChangeFunction,
        }),
    };
  };

  const clearStepsList = (props) => {
    const { listUpdateFunction = () => {}, modalCancelFunction = () => {} } =
      props;

    listUpdateFunction([]);

    modalCancelFunction();
  };

  const copyStepsListFromAnotherScenario = (props) => {
    const {
      scenarioId = "",
      listUpdateFunction = () => {},
      stepsType = "",
      modalCancelFunction = () => {},
    } = props;

    const scenario = curScenariosList?.find(
      (elem) => elem?.scenarioID === scenarioId,
    );
    let steps = [];

    switch (stepsType) {
      case "steps":
        steps = scenario?.steps || [];
        break;
      case "cookies":
        steps = scenario?.cookiesAcceptance?.steps || [];
        break;
      case "login":
        steps = scenario?.loginInfo?.steps || [];
        break;
    }

    listUpdateFunction(steps);

    modalCancelFunction();
  };

  // DataLayer template modal
  const [selectedStepId, setSelectedStepId] = useState(null);
  // Action type of the step the template drawer was opened for. Drives the
  // cookie-specific fields/labels in NestedObjectTable for check_cookie_value.
  const [selectedStepType, setSelectedStepType] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateStr, setSelectedTemplateStr] = useState(null);
  const [templateSaveStatus, setTemplateSaveStatus] = useState(false);
  const [showTemplateUnsavedModal, setShowTemplateUnsavedModal] =
    useState(false);
  const initialTemplateRef = useRef(null);

  const menuProps = {
    items: defaultMenuProps,
    onClick: (e) => addDefaultEcommerceObject(e?.key),
  };

  const clearJSON = () => {
    setSelectedTemplate(null);
    setSelectedTemplateStr(null);
  };

  const addDefaultEcommerceObject = (eventName) => {
    const obj = defaultEventTemplates[eventName];
    setSelectedTemplate(obj);
    setSelectedTemplateStr(JSON.stringify(obj));
  };

  // edit json modal window
  const [dlEditModalTitle, setDlEditModalTitle] = useState("");
  const [dlEditModalState, setDlEditModalState] = useState(false);
  const [dlEditModalContent, setDlEditModalContent] = useState(false);
  const [editedTextfieldValue, setEditedTextfieldValue] = useState("");
  const defaultEventTemplate = { "": "" };

  useEffect(() => {
    const initialTemplate =
      selectedTemplate?.length === 1 &&
      selectedTemplate[0].key === "" &&
      selectedTemplate[0].value === "";

    const text = initialTemplate ? "add json" : "edit json";
    const icon = initialTemplate ? <PlusOutlined /> : <EditFilled />;

    setJsonBtnText(text);
    setJsonBtnIcon(icon);
  }, [selectedTemplate]);

  useEffect(() => {
    if (!thirdDrawerVisible) return;

    const hasChanges = !_.isEqual(
      initialTemplateRef.current || [],
      selectedTemplate || [],
    );
    setTemplateSaveStatus(hasChanges);
  }, [selectedTemplate, thirdDrawerVisible]);

  // Modal window event handlers
  const handleEditDlModalOk = () => {
    try {
      const value = jsonrepair(
        editedTextfieldValue || JSON.stringify(defaultEventTemplate),
      );
      saveEditedDlObjectData(value);
    } catch (err) {
      console.log(err);
      error("Incorrect value! Please check the JSON object and try again.");
    }
  };
  const saveEditedDlObjectData = (value) => {
    const transformedTextFieldValue = transformDataObject(JSON.parse(value));
    setSelectedTemplate(transformedTextFieldValue);
    setSelectedTemplateStr(JSON.stringify(transformedTextFieldValue));

    handleSaveTemplate(transformedTextFieldValue);

    setDlEditModalState(false);
  };
  const handleEditDlModalCancel = () => setDlEditModalState(false);

  const handleEditTemplateEvent = () => {
    setDlEditModalContent(defaultModalContent());
    setDlEditModalState(true);
  };

  const defaultModalContent = () => {
    const placeholderObj = {
      event: "event_name",
      ecommerce: {
        currency: "string",
        value: "number",
        items: [
          {
            item_id: "string",
            item_name: "string",
          },
        ],
      },
    };
    const placeholderText = JSON.stringify(placeholderObj, undefined, 2);

    const defaultValueTemplate = simplifyDLGEventJson(selectedTemplate);
    const defaultValueStr = JSON.stringify(defaultValueTemplate, null, 2);
    setEditedTextfieldValue(defaultValueStr);

    setDlEditModalTitle("Template DataLayer object");

    return (
      <>
        <p>Add your dataLayer template in the box below.</p>
        <TextArea
          name="datalayer_object_input"
          key={editedTextfieldValue}
          rows={12}
          placeholder={placeholderText}
          defaultValue={defaultValueStr}
          onInput={(e) => setEditedTextfieldValue(e.target.value)}
          style={{
            fontFamily: "monospace",
            fontSize: "14px",
            backgroundColor: "#f7f7f7",
            border: "1px solid #ccc",
            padding: "10px",
            borderRadius: "4px",
            whiteSpace: "pre-wrap",
          }}
        />
      </>
    );
  };

  // Saving data
  const handleSaveTemplate = (template = []) => {
    const updatedSteps = scenarioSteps.map((elem) => {
      switch (elem.stepId === selectedStepId) {
        case true:
          elem.dataLayerObjToFind = template;
          return elem;

        default:
          return elem;
      }
    });

    const templateSimplified = simplifyDLGEventJson(template);
    const templateStr = JSON.stringify(templateSimplified);
    setEditedTextfieldValue(templateStr);

    setSelectedTemplate(template);
    setSelectedTemplateStr(JSON.stringify(template));
    try {
      initialTemplateRef.current = JSON.parse(JSON.stringify(template));
    } catch (err) {
      initialTemplateRef.current = template;
    }
    setTemplateSaveStatus(false);
    handleSaveSteps(updatedSteps);
  };

  const renumberSteps = (steps) => steps.map((s, idx) => ({ ...s, step: idx + 1 }));
  const handleSaveSteps = (steps = []) => {
    setScenarioSteps(steps);
    const newScenarioObj = selectedScenario;
    newScenarioObj.steps = renumberSteps(steps);
    setSelectedScenario(newScenarioObj);

    return handleSaveScenarios({ stepsOverride: steps });
  };

  // Returns the freshly built scenarios list so callers don't have to
  // read it back through the async-updated `curScenariosList` state.
  const handleSaveScenarios = ({ stepsOverride } = {}) => {
    if (!selectedScenario) return curScenariosList;

    const newScenarioObj = selectedScenario;
    newScenarioObj.scenarioTitle = scenarioTitle;
    newScenarioObj.active = scenarioStatus;
    newScenarioObj.urlStartingPoint = scenarioStartPage;
    newScenarioObj.urlStartingPointMatchType = scenarioStartPageMatchType;
    newScenarioObj.urlStartingPointPageType = scenarioStartPageType;
    newScenarioObj.steps = stepsOverride ?? renumberSteps(scenarioSteps);

    const newCookiesObj = { ...scenarioCookies };
    newCookiesObj.active = scenarioCookiesStatus;
    newCookiesObj.steps = scenarioCookiesSteps;
    setScenarioCookies(newCookiesObj);

    const newLoginObj = { ...scenarioLogin };
    newLoginObj.required = scenarioLoginStatus;
    newLoginObj.steps = scenarioLoginSteps;
    setScenarioLogin(newLoginObj);

    newScenarioObj.cookiesAcceptance = newCookiesObj;
    newScenarioObj.loginInfo = newLoginObj;

    const newScenariosList = curScenariosList.map((elem) =>
      selectedScenario.scenarioID === elem.scenarioID ? newScenarioObj : elem,
    );

    setCurScenariosList(newScenariosList);
    return newScenariosList;
  };

  const saveScenarioStepsWithGuard = async () => {
    if (isScenarioSaving || !urlTldValid || !scenarioSaveStatus) {
      return;
    }

    setIsScenarioSaving(true);

    try {
      const newScenariosList = handleSaveSteps(scenarioSteps);
      const newConfigParams = { ...configParams, scenarios: newScenariosList };

      setConfigParams(newConfigParams);
      const saveSucceeded = await saveFunction(newConfigParams);

      if (!saveSucceeded) {
        return;
      }

      initialScenarioRef.current = {
        title: scenarioTitle,
        startPage: scenarioStartPage,
        matchType: scenarioStartPageMatchType,
        pageType: scenarioStartPageType,
        steps: _.cloneDeep(scenarioSteps),
      };
      setScenarioSaveStatus(false);
      closeSecondDrawer();
    } finally {
      setIsScenarioSaving(false);
    }
  };

  const handleSaveAction = (level) => {
    let freshScenarios = curScenariosList;
    switch (level) {
      case "templates":
        handleSaveTemplate(selectedTemplate);
        closeThirdDrawer();
        break;
      case "steps":
        freshScenarios = handleSaveSteps(scenarioSteps);
        closeSecondDrawer();
        break;
      case "cookies":
        freshScenarios = handleSaveScenarios();
        closeCookiesDrawer();
        break;
      case "login":
        freshScenarios = handleSaveScenarios();
        closeLoginDrawer();
        break;
      case "scenarios":
        freshScenarios = handleSaveScenarios();
        setDrawerGlobalState(false);
        break;
    }

    const newConfigParams = { ...configParams, scenarios: freshScenarios };
    setConfigParams(newConfigParams);
    setIsConfigParamsUpdatedBeforeSave(true);
  };

  // Data saving functionality control
  const [isConfigParamsUpdatedBeforeSave, setIsConfigParamsUpdatedBeforeSave] =
    useState(false);

  useEffect(() => {
    if (isConfigParamsUpdatedBeforeSave) {
      saveFunction();
      setIsConfigParamsUpdatedBeforeSave(false);
    }
  }, [isConfigParamsUpdatedBeforeSave]);

  // --- Component / Link resolution and detach ---

  // Overlay for components added in this session that aren't yet reflected
  // in the context-derived `reusableComponents` (which trails until the
  // next save → setCurrentConfig round-trip).
  useEffect(() => {
    if (!pendingComponents.length) return;
    const knownIds = new Set(reusableComponents.map((c) => c.componentId));
    const stillPending = pendingComponents.filter(
      (c) => !knownIds.has(c.componentId),
    );
    if (stillPending.length !== pendingComponents.length) {
      setPendingComponents(stillPending);
    }
  }, [reusableComponents, pendingComponents]);

  // Returns null when the source doesn't exist (used to drive the "stale"
  // warning), and an array (possibly empty) when it exists.
  const resolveComponentSteps = (componentId) => {
    const component =
      reusableComponents?.find((c) => c.componentId === componentId) ||
      pendingComponents.find((c) => c.componentId === componentId);
    if (!component) return null;
    return component.steps || [];
  };

  const resolveLinkedSteps = (scenarioId, stepsType) => {
    const scenario = curScenariosList?.find(
      (s) => s.scenarioID === scenarioId,
    );
    if (!scenario) return null;
    switch (stepsType) {
      case "steps":
        return scenario.steps?.filter((s) => !s.blockType) || [];
      case "cookies":
        return scenario.cookiesAcceptance?.steps?.filter((s) => !s.blockType) || [];
      case "login":
        return scenario.loginInfo?.steps?.filter((s) => !s.blockType) || [];
      default:
        console.warn(`resolveLinkedSteps: unknown stepsType "${stepsType}"`);
        return null;
    }
  };

  // Resolve the *current* display name for a block, so renames in the source
  // component / scenario propagate to existing references instead of showing
  // the snapshot taken at insert time. Returns null when the source no
  // longer exists; callers fall back to the name stored on the block.
  const getBlockDisplayName = (block) => {
    if (!block) return null;
    if (block.blockType === "component_ref") {
      const component =
        reusableComponents?.find((c) => c.componentId === block.componentId) ||
        pendingComponents.find((c) => c.componentId === block.componentId);
      return component ? component.componentName || "" : null;
    }
    if (block.blockType === "scenario_link") {
      const scenario = curScenariosList?.find(
        (s) => s.scenarioID === block.sourceScenarioId,
      );
      return scenario ? scenario.scenarioTitle || "" : null;
    }
    return null;
  };

  // Uses reference equality on the captured `block` so that two block_refs
  // pointing at the same component (and any legacy data with colliding
  // blockIds) still detach exactly the one the user clicked.
  const handleDetachBlock = (block, stepsListSetter, currentSteps) => {
    if (!block) return;
    const blockIndex = currentSteps.indexOf(block);
    if (blockIndex === -1) return;

    let resolvedSteps = [];
    if (block.blockType === "component_ref") {
      resolvedSteps = resolveComponentSteps(block.componentId) || [];
    } else if (block.blockType === "scenario_link") {
      resolvedSteps =
        resolveLinkedSteps(block.sourceScenarioId, block.sourceStepsType) || [];
    }

    const clonedSteps = resolvedSteps.map((step) => ({
      ...step,
      stepId: generateUniqueId(),
    }));

    stepsListSetter([
      ...currentSteps.slice(0, blockIndex),
      ...clonedSteps,
      ...currentSteps.slice(blockIndex + 1),
    ]);
  };

  const handleDeleteBlock = (block, stepsListSetter, currentSteps) => {
    if (!block) return;
    const blockIndex = currentSteps.indexOf(block);
    if (blockIndex === -1) return;
    stepsListSetter([
      ...currentSteps.slice(0, blockIndex),
      ...currentSteps.slice(blockIndex + 1),
    ]);
  };

  // Open the detach / delete confirmation modal. The block, the slot's setter,
  // and the snapshot of the current steps are captured here so that confirm
  // handlers can dispatch to the right slot without re-reading state.
  const requestDetachBlock = (block, stepsListSetter, currentSteps, slot) => {
    if (!block) return;
    setDetachTarget({ block, stepsListSetter, currentSteps, slot });
  };

  const requestDeleteBlock = (block, stepsListSetter, currentSteps, slot) => {
    if (!block) return;
    setDeleteTarget({ block, stepsListSetter, currentSteps, slot });
  };

  // Close the modal first (same render commit), then mutate the steps. The
  // detach/delete Modal lives at ScenariosMenu scope — which doesn't unmount
  // when the steps array changes — so antd's leave transition runs cleanly.
  const confirmDetach = () => {
    if (!detachTarget) return;
    const { block, stepsListSetter, currentSteps } = detachTarget;
    setDetachTarget(null);
    handleDetachBlock(block, stepsListSetter, currentSteps);
  };

  const cancelDetach = () => setDetachTarget(null);

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const { block, stepsListSetter, currentSteps } = deleteTarget;
    setDeleteTarget(null);
    handleDeleteBlock(block, stepsListSetter, currentSteps);
  };

  const cancelDelete = () => setDeleteTarget(null);

  // Block-source navigation: a `component_ref` peeks at that component
  // (component editor opens over the current scenario edit); a
  // `scenario_link` swaps the current scenario edit to the source scenario
  // and pushes a snapshot of the previous scenario's in-progress state onto
  // a back-stack. Closing pops the snapshot back so unsaved edits survive
  // the round-trip.
  const handleEditBlockSource = (block) => {
    if (block?.blockType === "component_ref" && block.componentId) {
      peekComponent(block.componentId);
    } else if (block?.blockType === "scenario_link" && block.sourceScenarioId) {
      if (selectedScenario?.scenarioID) {
        setLinkedScenarioStack((prev) => [
          ...prev,
          {
            scenarioID: selectedScenario.scenarioID,
            snapshot: {
              scenarioTitle,
              scenarioStatus,
              scenarioStartPage,
              scenarioStartPageMatchType,
              scenarioStartPageType,
              scenarioSteps,
              scenarioCookies,
              scenarioLogin,
              scenarioCookiesStatus,
              scenarioCookiesSteps,
              scenarioLoginStatus,
              scenarioLoginSteps,
              urlTldValid,
              urlTldError,
              // Shallow-copy so a later mutation on the live ref can't reach
              // back and rewrite the stack entry.
              initialScenarioRef: initialScenarioRef.current
                ? { ...initialScenarioRef.current }
                : null,
            },
          },
        ]);
      }
      handleEditScenarioClick(block.sourceScenarioId, { keepStack: true });
    }
  };

  // Insert component ref block into a steps list
  const handleInsertComponent = (componentId, stepsListSetter, currentSteps) => {
    const component = reusableComponents?.find(
      (c) => c.componentId === componentId,
    );
    if (!component) return;

    const block = {
      blockType: "component_ref",
      blockId: generateUniqueId(),
      componentId: component.componentId,
      componentName: component.componentName,
    };

    stepsListSetter([...currentSteps, block]);

    // Component is appended at the end of the list, which can be off-screen,
    // so make the action visible with a success toast.
    const name = component.componentName || "Component";
    success(`"${name}" inserted at the end of the steps list.`);
  };

  // Build dropdown items for the "Insert component" menu
  const componentsDropdownItems = reusableComponents.map((comp) => ({
    key: comp.componentId,
    label: comp.componentName || "Untitled Component",
  }));

  // --- Block detach / delete confirmation modal state ---
  // The Modal lives at this level (rather than inside StepBlockWrapper) so it
  // does not unmount when the underlying steps array mutates — otherwise the
  // wrapper for the just-detached block disappears in the same commit and
  // antd's Modal portal is left orphaned in the DOM.
  const [detachTarget, setDetachTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // --- Import steps modal state ---
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importSourceScenarioId, setImportSourceScenarioId] = useState(null);
  const [importTargetSetter, setImportTargetSetter] = useState(null);
  const [importTargetSteps, setImportTargetSteps] = useState([]);
  const [importStepsType, setImportStepsType] = useState("steps");

  const handleImportFromScenario = (scenarioId, stepsType, setter, currentSteps) => {
    setImportSourceScenarioId(scenarioId);
    setImportStepsType(stepsType);
    setImportTargetSetter(() => setter);
    setImportTargetSteps(currentSteps);
    setImportModalVisible(true);
  };

  // Detect circular live links: check if sourceId transitively links back to targetId
  const hasCircularLink = (sourceScenarioId, targetScenarioId, visited = new Set()) => {
    if (visited.has(sourceScenarioId)) return false;
    visited.add(sourceScenarioId);

    const source = curScenariosList?.find((s) => s.scenarioID === sourceScenarioId);
    if (!source) return false;

    const allSteps = [
      ...(source.steps || []),
      ...(source.cookiesAcceptance?.steps || []),
      ...(source.loginInfo?.steps || []),
    ];

    for (const item of allSteps) {
      if (item?.blockType === "scenario_link") {
        if (item.sourceScenarioId === targetScenarioId) return true;
        if (hasCircularLink(item.sourceScenarioId, targetScenarioId, visited)) return true;
      }
    }
    return false;
  };

  const getImportSourceSteps = () => {
    const scenario = curScenariosList?.find(
      (s) => s.scenarioID === importSourceScenarioId,
    );
    if (!scenario) return [];
    switch (importStepsType) {
      case "steps":
        return scenario.steps || [];
      case "cookies":
        return scenario.cookiesAcceptance?.steps || [];
      case "login":
        return scenario.loginInfo?.steps || [];
      default:
        return [];
    }
  };

  const handleImportConfirm = (mode, selectedStepIds = null) => {
    const scenario = curScenariosList?.find(
      (s) => s.scenarioID === importSourceScenarioId,
    );
    if (!scenario) return;

    const sourceSteps = getImportSourceSteps();
    if (mode === "copy" && !sourceSteps.length) return;

    if (mode === "copy") {
      const filtered = Array.isArray(selectedStepIds)
        ? sourceSteps.filter((s) =>
            selectedStepIds.includes(s.stepId || s.blockId),
          )
        : sourceSteps;
      const cloned = filtered.map((step) => {
        if (step?.blockType) {
          // Preserve block, regenerate blockId so it's a distinct instance
          return { ...step, blockId: generateUniqueId() };
        }
        return { ...step, stepId: generateUniqueId() };
      });
      if (importTargetSetter) {
        importTargetSetter([...importTargetSteps, ...cloned]);
      }
    } else if (mode === "link") {
      // Check for circular link before inserting
      if (
        selectedScenario &&
        hasCircularLink(importSourceScenarioId, selectedScenario.scenarioID)
      ) {
        error("Cannot create a circular link. The source scenario already links back to this scenario.");
        setImportModalVisible(false);
        setImportSourceScenarioId(null);
        return;
      }

      // Insert a live link block
      const block = {
        blockType: "scenario_link",
        blockId: generateUniqueId(),
        sourceScenarioId: importSourceScenarioId,
        sourceScenarioTitle: scenario.scenarioTitle || "Untitled Scenario",
        sourceStepsType: importStepsType,
      };
      if (importTargetSetter) {
        importTargetSetter([...importTargetSteps, block]);
      }
    }

    setImportModalVisible(false);
    setImportSourceScenarioId(null);
  };

  // --- Save as Component modal state ---
  const [saveAsComponentModalVisible, setSaveAsComponentModalVisible] = useState(false);
  const [saveAsComponentSteps, setSaveAsComponentSteps] = useState([]);

  const handleSaveAsComponent = () => {
    // Get only regular steps (not blocks) from current scenario
    const regularSteps = scenarioSteps.filter((s) => !s?.blockType);
    setSaveAsComponentSteps(regularSteps);
    setSaveAsComponentModalVisible(true);
  };

  const handleSaveAsComponentConfirm = (name, selectedStepIds, replaceWithRef) => {
    const selectedSteps = scenarioSteps
      .filter((s) => !s?.blockType && selectedStepIds.includes(s.stepId))
      .map((s) => ({ ...s, stepId: generateUniqueId() }));

    if (!selectedSteps.length || !name.trim()) return;

    const newComponent = {
      componentId: generateUniqueId(),
      componentName: name.trim(),
      description: "",
      steps: selectedSteps,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Add to reusable components (include any still-pending local additions
    // so they aren't dropped if the user saves multiple components in a row).
    const updatedComponents = [
      ...reusableComponents,
      ...pendingComponents,
      newComponent,
    ];

    if (replaceWithRef) {
      // Replace selected steps with a component reference
      const block = {
        blockType: "component_ref",
        blockId: generateUniqueId(),
        componentId: newComponent.componentId,
        componentName: newComponent.componentName,
      };

      // Remove selected steps and insert block at the position of the first selected step
      const firstSelectedIdx = scenarioSteps.findIndex(
        (s) => !s?.blockType && selectedStepIds.includes(s.stepId),
      );
      const filteredSteps = scenarioSteps.filter(
        (s) => s?.blockType || !selectedStepIds.includes(s.stepId),
      );
      const newSteps = [
        ...filteredSteps.slice(0, firstSelectedIdx),
        block,
        ...filteredSteps.slice(firstSelectedIdx),
      ];
      setScenarioSteps(newSteps);
    }

    // Make the new component resolvable immediately via the overlay. The
    // component will be persisted on the user's next explicit Save — we
    // intentionally don't trigger a save here, because `scenarioSteps` edits
    // haven't been written back to `selectedScenario` / `curScenariosList`
    // yet, so a save at this point would commit a stale scenarios list.
    setPendingComponents((prev) => [...prev, newComponent]);

    const updated = { ...configParams, reusable_components: updatedComponents };
    setConfigParams(updated);
    setSaveAsComponentModalVisible(false);
  };

  return (
    <div>
      {/* Scenario drawer menu. */}
      <Drawer
        className="scenarios-drawer"
        placement={defaultPlacement}
        onClose={() => setDrawerGlobalState(false)}
        open={drawerGlobalState}
        forceRender
        zIndex={50}
        closeIcon={null}
        closable={true}
        width={firstDrawerWidth}
        styles={{ header: { display: "none" } }}
      >
        <div className={`modal-elems-rows scenarios-modal-padding`}>
          <div>
            <h2 className="header-letter-spacing">
              Monitoring via page scenarios
            </h2>
          </div>
          <div
            style={{ marginBottom: 0 }}
            className="container-guide modal-panel-button-row"
          >
            <Link
              className="modal-guide-banner "
              target="_blank"
              to={pageScenariosDescLink}
              style={{ textDecoration: "none", color: "white" }}
            >
              <div className="file-icon">
                <svg
                  width="30"
                  height="36"
                  viewBox="0 0 30 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_d_190_157)">
                    <path
                      d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                      fill="#69C4FF"
                      fillOpacity="0.5"
                    />
                    <path
                      d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                      fill="#69C4FF"
                      fillOpacity="0.5"
                    />
                    <path
                      d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                      fill="#DBF1FF"
                    />
                    <path
                      d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                      fill="#B0E0FF"
                    />
                    <path
                      d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                      fill="white"
                    />
                    <line
                      x1="8.82252"
                      y1="9.40248"
                      x2="19.988"
                      y2="9.40248"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.90306"
                      y1="15.1451"
                      x2="20.0685"
                      y2="15.1451"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.82252"
                      y1="11.3499"
                      x2="19.988"
                      y2="11.3499"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.90306"
                      y1="17.0925"
                      x2="20.0685"
                      y2="17.0925"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <line
                      x1="8.82252"
                      y1="13.1675"
                      x2="19.988"
                      y2="13.1675"
                      stroke="#009BFF"
                      strokeOpacity="0.14"
                      strokeWidth="0.389493"
                      strokeLinecap="round"
                    />
                    <path
                      d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                      fill="#69C4FF"
                      fillOpacity="0.14"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_d_190_157"
                      x="0.255097"
                      y="0.170166"
                      width="24.9498"
                      height="30.5854"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="1.74496" />
                      <feGaussianBlur stdDeviation="0.872482" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_190_157"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_190_157"
                        result="shape"
                      />
                    </filter>
                  </defs>
                </svg>
              </div>
              <div className="panel-button-text">
                <div className="panel-button-desc">
                  Click here to follow the complete setup guide for the
                  DataLayer Guard.
                  <br /> Question not covered?{" "}
                  <span
                    role="link"
                    tabIndex={0}
                    className="support_request_link"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(
                        "https://portal.code-cube.io/support_request",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(
                          "https://portal.code-cube.io/support_request",
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    }}
                  >
                    Please get in contact.
                  </span>
                </div>
              </div>
            </Link>
          </div>
          <div className="modal-text">
            <p>
              This page provides an overview of all scenarios that DataLayer
              Guard should follow to collect dataLayer events. Each scenario
              should replicate the steps taken by a typical user.
            </p>
            <p>
              You can configure multiple scenarios to ensure comprehensive event
              monitoring.
            </p>
          </div>

          {/* Table with scenarios list. */}
          <div className="no-sep-header">
            <Table
              size="small"
              columns={scenarioColumns}
              dataSource={curScenariosList}
              rowKey="scenarioID"
              rowClassName="scenarios-list-row"
              onRow={(record) => ({
                onClick: () => handleEditScenarioClick(record?.scenarioID),
              })}
              pagination={false}
            />
          </div>
              
          <div
            style={{
              marginTop: "32px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              gap: "18px",
            }}
          >
            {/* add scenario button */}
            <Button type="default" onClick={handleAddScenarioClick}>
              + Add scenario
            </Button>

            {/* Test scenario button */}
            <Button type="default" onClick={testScenario} style={{ width: 'fit-content' }}>
              Test scenario
            </Button>
            <TestScenarioWindow testModalOpen={testModalOpen} setTestModalOpen={setTestModalOpen} scenarioId={testScenarioId} scenarios={curScenariosList || []} onOpenScenario={handleOpenScenarioAtStep}/>

            {/* manage consent settings button */}
            {/* <Button type="default" onClick={handleManageConsentSettings}>
              + manage consent settings
            </Button> */}
          </div>
          <Modal
            centered
            open={deleteModalVisible}
            title="Delete this scenario?"
            onOk={handleDeleteConfirmed}
            onCancel={() => {
              setDeleteModalVisible(false);
              setTargetScenarioId(null);
            }}
            okButtonProps={{ danger: true }}
          >
            Are you sure you want to delete this scenario?
          </Modal>
          <Modal
            centered
            open={pauseModalVisible}
            title="Pause this scenario?"
            onOk={handlePauseConfirmed}
            onCancel={() => {
              setPauseModalVisible(false);
              setTargetScenarioId(null);
            }}
            okButtonProps={{ danger: true }}
          >
            <p>Are you sure you want to pause this scenario?</p>
          </Modal>
        </div>

        {/* Scenario steps modal window. */}
        <Drawer
          className="scenarios-drawer"
          // Slide this drawer left when a component peek is open. The peek
          // renders ComponentsMenu's editor in a separate render tree, so
          // antd's push can't reach it — we apply the shift via CSS instead.
          classNames={peekComponentId ? { wrapper: "scenarios-drawer-peek-shifted" } : undefined}
          placement={defaultPlacement}
          push={{ distance: 240 }}
          onClose={() => {
            // While a child modal is open, the drawer's close trigger
            // (Esc / mask click) belongs to the topmost modal — dismiss
            // that instead of closing the drawer.
            if (detachTarget) {
              setDetachTarget(null);
              return;
            }
            if (deleteTarget) {
              setDeleteTarget(null);
              return;
            }
            if (importModalVisible) {
              setImportModalVisible(false);
              setImportSourceScenarioId(null);
              return;
            }
            if (saveAsComponentModalVisible) {
              setSaveAsComponentModalVisible(false);
              return;
            }
            if (showUnsavedModal) {
              setShowUnsavedModal(false);
              return;
            }
            if (stepsListChangeModalState) {
              handleStepsListModalCancel();
              return;
            }
            if (scenarioSaveStatus) {
              setShowUnsavedModal(true);
            } else {
              closeSecondDrawer();
            }
          }}
          open={secondDrawerVisible}
          zIndex={1410}
          closeIcon={null}
          closable={true}
          width={secondDrawerWidth}
          styles={{ header: { display: "none" } }}
        >
          <ConfirmationModalWindow
            open={stepsListChangeModalState}
            onOk={stepsListChangeModalOkFunction}
            onCancel={handleStepsListModalCancel}
            text={"Are you sure you want to update the steps list?"}
          />

          {/* Detach block confirmation modal — owned here (not inside
              StepBlockWrapper) so it survives the wrapper unmounting when
              the block gets replaced by its resolved steps. */}
          {(() => {
            const target = detachTarget;
            const isComp = target?.block?.blockType === "component_ref";
            const label = isComp ? "component" : "live link";
            const liveName = target ? getBlockDisplayName(target.block) : null;
            const stored = isComp
              ? target?.block?.componentName
              : target?.block?.sourceScenarioTitle;
            const name =
              (liveName != null ? liveName : stored) ||
              (isComp ? "Unnamed Component" : "Unnamed Scenario");
            return (
              <Modal
                centered
                open={!!target}
                title={`Detach ${label}?`}
                onOk={confirmDetach}
                onCancel={cancelDetach}
                okText="Detach"
                okType="saveChanges"
                zIndex={2000}
              >
                <p>
                  This will detach <strong>"{name}"</strong> from this scenario.
                  The steps will become independent and will no longer update
                  when the {isComp ? "component" : "source scenario"} changes.
                </p>
                <p>Do you want to continue?</p>
              </Modal>
            );
          })()}

          {/* Delete block confirmation modal — owned here for the same reason
              as the detach modal. */}
          {(() => {
            const target = deleteTarget;
            const isComp = target?.block?.blockType === "component_ref";
            const label = isComp ? "component" : "live link";
            const liveName = target ? getBlockDisplayName(target.block) : null;
            const stored = isComp
              ? target?.block?.componentName
              : target?.block?.sourceScenarioTitle;
            const name =
              (liveName != null ? liveName : stored) ||
              (isComp ? "Unnamed Component" : "Unnamed Scenario");
            return (
              <Modal
                centered
                open={!!target}
                title={`Delete ${label}?`}
                onOk={confirmDelete}
                onCancel={cancelDelete}
                okText="Delete"
                okType="saveChanges"
                zIndex={2000}
              >
                <p>
                  This will remove <strong>"{name}"</strong> from this scenario.
                  The {isComp ? "component" : "source scenario"} itself will not
                  be deleted and remains available for reuse.
                </p>
                <p>Do you want to continue?</p>
              </Modal>
            );
          })()}

          {/* Unsaved changes confirmation modal */}
          <Modal
            open={showUnsavedModal}
            centered
            title={
              <div className="closeIconDiv">
                <span>Save your changes</span>
                <CloseOutlined
                  onClick={() => setShowUnsavedModal(false)}
                  className="closeIcon"
                />
              </div>
            }
            onCancel={() => setShowUnsavedModal(false)}
            closable={false}
            footer={[
              <Button
                key="discard"
                type="default"
                disabled={isScenarioSaving}
                onClick={() => {
                  resetScenarioDraftToLastSaved();
                  setShowUnsavedModal(false);
                  closeSecondDrawer();
                }}
              >
                discard
              </Button>,
              <Button
                key="save"
                type="saveChanges"
                disabled={!urlTldValid || isScenarioSaving}
                onClick={async () => {
                  setShowUnsavedModal(false);
                  await saveScenarioStepsWithGuard();
                }}
              >
                save changes
              </Button>,
            ]}
          >
            <p>Do you want to save changes before leaving this step?</p>
          </Modal>

          {/* Import Steps Modal - Copy vs Live Link choice */}
          <ImportStepsModal
            visible={importModalVisible}
            scenarioTitle={
              curScenariosList?.find(
                (s) => s.scenarioID === importSourceScenarioId,
              )?.scenarioTitle || "Untitled Scenario"
            }
            sourceSteps={importModalVisible ? getImportSourceSteps() : []}
            getBlockDisplayName={getBlockDisplayName}
            onConfirm={handleImportConfirm}
            onCancel={() => {
              setImportModalVisible(false);
              setImportSourceScenarioId(null);
            }}
          />

          {/* Save as Component Modal */}
          <SaveAsComponentModal
            visible={saveAsComponentModalVisible}
            steps={saveAsComponentSteps}
            onConfirm={handleSaveAsComponentConfirm}
            onCancel={() => setSaveAsComponentModalVisible(false)}
          />

          <div
            key={isConfigParamsUpdatedBeforeSave}
            className={`modal-elems-rows scenarios-modal-padding`}
          >
            <div>
              <h2 className="header-letter-spacing">
                Set-up details for DataLayer Guard scenario
              </h2>
            </div>
            {/* {scenarioEvents?.map((event, idx) => (
                                event?.eventName ?
                                    <Tag key={idx}>{event?.stepNum} - {event?.eventName}</Tag>
                                    : undefined
                            ))}
                        </div> */}
            <div
              style={{ marginBottom: 0 }}
              className="container-guide modal-panel-button-row"
            >
              <Link
                className="modal-guide-banner "
                target="_blank"
                to={pageScenariosDescLink}
                style={{ textDecoration: "none", color: "white" }}
              >
                <div className="file-icon">
                  <svg
                    width="30"
                    height="36"
                    viewBox="0 0 30 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g filter="url(#filter0_d_190_157)">
                      <path
                        d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                        fill="#69C4FF"
                        fillOpacity="0.5"
                      />
                      <path
                        d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                        fill="#69C4FF"
                        fillOpacity="0.5"
                      />
                      <path
                        d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                        fill="#DBF1FF"
                      />
                      <path
                        d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                        fill="#B0E0FF"
                      />
                      <path
                        d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                        fill="white"
                      />
                      <line
                        x1="8.82252"
                        y1="9.40248"
                        x2="19.988"
                        y2="9.40248"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.90306"
                        y1="15.1451"
                        x2="20.0685"
                        y2="15.1451"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.82252"
                        y1="11.3499"
                        x2="19.988"
                        y2="11.3499"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.90306"
                        y1="17.0925"
                        x2="20.0685"
                        y2="17.0925"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8.82252"
                        y1="13.1675"
                        x2="19.988"
                        y2="13.1675"
                        stroke="#009BFF"
                        strokeOpacity="0.14"
                        strokeWidth="0.389493"
                        strokeLinecap="round"
                      />
                      <path
                        d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                        fill="#69C4FF"
                        fillOpacity="0.14"
                      />
                    </g>
                    <defs>
                      <filter
                        id="filter0_d_190_157"
                        x="0.255097"
                        y="0.170166"
                        width="24.9498"
                        height="30.5854"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset dy="1.74496" />
                        <feGaussianBlur stdDeviation="0.872482" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                        />
                        <feBlend
                          mode="normal"
                          in2="BackgroundImageFix"
                          result="effect1_dropShadow_190_157"
                        />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="effect1_dropShadow_190_157"
                          result="shape"
                        />
                      </filter>
                    </defs>
                  </svg>
                </div>
                <div className="panel-button-text">
                  <div className="panel-button-desc">
                    Click here to follow the complete setup guide for the
                    DataLayer Guard.
                    <br /> Question not covered?{" "}
                    <span
                      role="link"
                      tabIndex={0}
                      className="support_request_link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(
                          "https://portal.code-cube.io/support_request",
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(
                            "https://portal.code-cube.io/support_request",
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      }}
                    >
                      Please get in contact.
                    </span>
                  </div>
                </div>
              </Link>
            </div>
            <div className="modal-text">
              <p>
                This page provides the details of the DataLayer Guard scenario.
                Make sure the scenario replicates the steps taken by an actual
                user. Please navigate back to the overview to add more
                scenarios.
              </p>
            </div>

            {/* Scenario title and starting point. */}
            <Space size={40} direction="vertical" style={{ width: "100%" }}>
              <Space direction="vertical" size={4}>
                <h3 className="modal-category">Scenario name</h3>
                <div className="modal-text">
                  Give your scenario a descriptive name.
                </div>
                <div style={{ marginTop: 16 }}>
                  <Input
                    name="scenario_title"
                    value={scenarioTitle}
                    style={{
                      width: "10rem",
                    }}
                    onChange={(e) => setScenarioTitle(e.target.value)}
                  />
                </div>
              </Space>
              <Space direction="vertical" size={4}>
                <h3 className="modal-category">Starting point for scenario</h3>
                <div className="modal-text">
                  Define where the scenario should start.
                </div>
                <div style={{ marginTop: 16 }}>
                  <Space size={5}>
                    <DropdownMenu
                      type="default"
                      text={
                        scenarioStartPageTypes.find(
                          (e) => e.key === scenarioStartPageType,
                        )?.label || "Select page type"
                      }
                      menuProps={menuPageTypes}
                      customStyle={{
                        background: "#fff",
                        color: "rgba(0,0,0,0.85)",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        height: 32,
                        padding: "4px 11px",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    />
                    <DropdownMenu
                      type="default"
                      text={
                        scenarioStartPageMatchTypes.find(
                          (e) => e.key === scenarioStartPageMatchType,
                        )?.label || "Select match type"
                      }
                      menuProps={menuMatchTypes}
                      customStyle={{
                        background: "#fff",
                        color: "rgba(0,0,0,0.85)",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        height: 32,
                        padding: "4px 11px",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    />
                    <div
                      style={{
                        position: "relative",
                      }}
                    >
                      <Input
                        name="scenario_start_page"
                        value={scenarioStartPage}
                        placeholder="/tag-monitor"
                        status={!urlTldValid ? "error" : ""}
                        style={{
                          width: "20rem",
                        }}
                        onChange={handleStartingPointUrlChange}
                      />
                      {!urlTldValid && urlTldError && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            color: "#ff4d4f",
                            fontSize: "12px",
                            marginTop: "4px",
                            lineHeight: "18px",
                          }}
                        >
                          {urlTldError}
                        </div>
                      )}
                    </div>
                  </Space>
                </div>
              </Space>
            </Space>

            {/* Cookies acceptance & Login required — fixed order, placed
                under Starting point. Each column has a fixed width so the
                Login section stays anchored when Cookies is toggled (the
                Edit button appears/disappears inside the Cookies column
                only, not shifting Login). */}
            <Flex gap={60} style={{ marginTop: 32 }}>
              <Space
                direction="vertical"
                size={4}
                style={{ width: 280, flex: "0 0 280px" }}
              >
                <h3 className="modal-category">Cookies acceptance</h3>
                <Space size={16}>
                  <Space>
                    <Switch
                      checked={scenarioCookiesStatus}
                      onChange={(value) => setScenarioCookiesStatus(value)}
                    />
                    <label>
                      {scenarioCookiesStatus ? "Required" : "Not required"}
                    </label>
                  </Space>
                  {scenarioCookiesStatus && (
                    <Button type="default" onClick={showCookiesDrawer}>
                      Edit cookies steps
                    </Button>
                  )}
                </Space>
              </Space>

              <Space
                direction="vertical"
                size={4}
                style={{ width: 280, flex: "0 0 280px" }}
              >
                <h3 className="modal-category">Login required</h3>
                <Space size={16}>
                  <Space>
                    <Switch
                      checked={scenarioLoginStatus}
                      onChange={(value) => setScenarioLoginStatus(value)}
                    />
                    <label>
                      {scenarioLoginStatus ? "Required" : "Not required"}
                    </label>
                  </Space>
                  {scenarioLoginStatus && (
                    <Button type="default" onClick={showLoginDrawer}>
                      Edit login steps
                    </Button>
                  )}
                </Space>
              </Space>
            </Flex>

            {/* Dynamically sortable steps list. */}
            <Space direction="vertical" size={4} style={{ marginTop: 40 }}>
              <h3 className="modal-category">Steps to take</h3>
              <div className="modal-text">
                Define the steps the scenario should follow.
              </div>

              {/* Toolbar: Import, Insert Component, Save as Component */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginTop: 8,
                  marginBottom: 4,
                }}
              >
                <DropdownMenu
                  text="Import steps"
                  type=""
                  icon={<CopyOutlined />}
                  menuProps={{
                    items: curScenariosList
                      ?.filter((s) => s.scenarioID !== selectedScenario?.scenarioID)
                      .map((s, idx) => ({
                        key: s.scenarioID,
                        label: s.scenarioTitle || `Untitled Scenario ${idx + 1}`,
                      })) || [],
                    onClick: (e) =>
                      handleImportFromScenario(
                        e?.key,
                        "steps",
                        setScenarioSteps,
                        scenarioSteps,
                      ),
                  }}
                />
                {componentsDropdownItems.length > 0 && (
                  <DropdownMenu
                    text="Insert component"
                    type=""
                    icon={<PlusOutlined />}
                    menuProps={{
                      items: componentsDropdownItems,
                      onClick: (e) =>
                        handleInsertComponent(
                          e?.key,
                          setScenarioSteps,
                          scenarioSteps,
                        ),
                    }}
                  />
                )}
                {scenarioSteps.some((s) => !s?.blockType) && (
                  <Button
                    size="small"
                    onClick={handleSaveAsComponent}
                  >
                    Save as component
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() =>
                    handleStepsListModalChangeState({
                      newState: true,
                      okFunction: () => {
                        setScenarioSteps([]);
                        handleStepsListModalCancel();
                      },
                      modalStateChangeFunction: setStepsListChangeModalState,
                    })
                  }
                >
                  Clear steps
                </Button>
              </div>

              <div style={{ marginTop: 16 }}>
                <SortableList
                  items={scenarioSteps}
                  handleDataLayerObjEdit={showThirdDrawer}
                  handleStepsUpdate={setScenarioSteps}
                  resolveComponentSteps={resolveComponentSteps}
                  resolveLinkedSteps={resolveLinkedSteps}
                  getBlockDisplayName={getBlockDisplayName}
                  onDetachBlock={(blockId) => {
                    const block = scenarioSteps.find(
                      (s) => s?.blockId === blockId,
                    );
                    requestDetachBlock(
                      block,
                      setScenarioSteps,
                      scenarioSteps,
                      "steps",
                    );
                  }}
                  onDeleteBlock={(blockId) => {
                    const block = scenarioSteps.find(
                      (s) => s?.blockId === blockId,
                    );
                    requestDeleteBlock(
                      block,
                      setScenarioSteps,
                      scenarioSteps,
                      "steps",
                    );
                  }}
                  onEditSource={handleEditBlockSource}
                />
              </div>
            </Space>
          </div>

          {/* Save button for scenario steps */}
          <div className="drawer-buttons-area">
            <div className="drawer-buttons-area-shad"></div>
            <div className="drawer-buttons-area-button" style={{ display: "flex", gap: "12px" }}>
              <Button
                size="large"
                disabled={isScenarioSaving}
                onClick={() => testSingleScenario(selectedScenario?.scenarioID)}
              >
                Test this scenario
              </Button>
              <ActionButton
                text={isScenarioSaving ? "Saving" : "Save"}
                size="large"
                type="save"
                width={140}
                loading={isScenarioSaving}
                disabled={
                  isScenarioSaving || !urlTldValid || !scenarioSaveStatus
                }
                onClickFunction={saveScenarioStepsWithGuard}
              />
              {scenarioSaveStatus && (
                <ActionButton
                  text="Cancel"
                  size="large"
                  type="default"
                  width={140}
                  disabled={isScenarioSaving}
                  onClickFunction={resetScenarioDraftToLastSaved}
                />
              )}
            </div>
          </div>

          {/* Modal window confirmation for deleting step. */}
          <Modal
            centered
            open={scenarioRemoveConfirmation}
            title="Confirm"
            onOk={() => scenarioRemove(selectedScenario?.scenarioID)}
            onCancel={() => setScenarioRemoveConfirmation(false)}
            footer={(_, { OkBtn, CancelBtn }) => (
              <Flex gap={5}>
                <CancelBtn />
                <OkBtn />
              </Flex>
            )}
          >
            <p>Do you want to delete this scenario?</p>
          </Modal>

          {/* DataLayer object template edit modal window. */}
          <Drawer
            key={templateModalContentCode}
            classNames={
              thirdDrawerClassName
                ? { wrapper: thirdDrawerClassName }
                : undefined
            }
            title={thirdDrawerTitle}
            placement={defaultPlacement}
            onClose={() => {
              const hasUnsavedTemplateChanges = !_.isEqual(
                initialTemplateRef.current || [],
                selectedTemplate || [],
              );

              if (hasUnsavedTemplateChanges) {
                setShowTemplateUnsavedModal(true);
              } else {
                closeThirdDrawer();
              }
            }}
            open={thirdDrawerVisible}
            closable={true}
            width={thirdDrawerWidth}
            styles={{ header: { display: "none" } }}
          >
            <Modal
              open={showTemplateUnsavedModal}
              centered
              title={
                <div className="closeIconDiv">
                  <span>Save your changes</span>
                  <CloseOutlined
                    onClick={() => setShowTemplateUnsavedModal(false)}
                    className="closeIcon"
                  />
                </div>
              }
              onCancel={() => setShowTemplateUnsavedModal(false)}
              closable={false}
              footer={[
                <Button
                  key="discard"
                  type="default"
                  onClick={() => {
                    if (initialTemplateRef.current) {
                      setSelectedTemplate(initialTemplateRef.current);
                      setSelectedTemplateStr(
                        JSON.stringify(initialTemplateRef.current),
                      );
                    }
                    setShowTemplateUnsavedModal(false);
                    setTemplateSaveStatus(false);
                    closeThirdDrawer();
                  }}
                >
                  discard
                </Button>,
                <Button
                  key="save"
                  type="saveChanges"
                  onClick={() => {
                    setShowTemplateUnsavedModal(false);
                    setTemplateSaveStatus(false);
                    handleSaveAction("templates");
                  }}
                >
                  save changes
                </Button>,
              ]}
            >
              <p>Do you want to save changes before leaving this step?</p>
            </Modal>

            {/* Modal window for editing the JSON-object representation of the template. */}
            <Modal
              centered
              title={dlEditModalTitle}
              open={dlEditModalState}
              onOk={handleEditDlModalOk}
              onCancel={handleEditDlModalCancel}
              footer={(_, { OkBtn, CancelBtn }) => (
                <Flex gap={5}>
                  <CancelBtn />
                  <OkBtn />
                </Flex>
              )}
            >
              {dlEditModalContent}
            </Modal>

            <div
              key="template_main"
              className={`modal-elems-rows scenarios-modal-padding ${
                templateModalContentCode === "main" ? "visible" : "hidden"
              }`}
            >
              <div>
                <h2 className="header-submodal header-letter-spacing">
                  DataLayer event template
                </h2>
              </div>

              <div>
                <h3 className="modal-category fw-bold">Add event details</h3>
                <p className="mb-0">
                  To enable monitoring for this event, details on the dataLayer
                  event should be shared in the table below.
                  <br></br>
                  For a quick start you can can copy / paste your JSON object
                  via the button below.
                </p>
              </div>

              {/* Buttons to edit template, add ecommerce event or clear template content. */}
              <Flex gap={10}>
                <ActionButton
                  text={jsonBtnText}
                  type=""
                  icon={jsonBtnIcon}
                  onClickFunction={() => {
                    setTemplateModalContentCode("edit");
                    handleEditTemplateEvent();
                  }}
                  width={120}
                />
                <DropdownMenu
                  text="add ecommerce event"
                  type=""
                  disabled={selectedTemplate?.length > 1}
                  menuProps={menuProps}
                  loading={false}
                  style={{ padding: "12px 20px" }}
                  icon={<DownOutlined />}
                />
                <ActionButton
                  text="clean json"
                  type=""
                  icon={<RedoOutlined />}
                  onClickFunction={clearJSON}
                />
              </Flex>

              {/* JSON object structure table */}
              <Space>
                <NestedObjectTable
                  key={selectedStepId}
                  data={selectedTemplateStr}
                  stepType={selectedStepType}
                  updateTemplateFunction={setSelectedTemplate}
                  setEditedEventTemplateNewVersion={setSelectedTemplate}
                />
              </Space>
            </div>

            <div
              key="template_edit"
              className={`modal-elems-rows scenarios-modal-padding ${
                templateModalContentCode === "edit" ? "visible" : "hidden"
              }`}
            >
              <div>
                <h2 className="header-submodal header-letter-spacing">
                  Edit DataLayer event template
                </h2>
              </div>

              <div>
                <h3 className="modal-category fw-bold">Add event details</h3>
                <p className="mb-0">
                  To enable monitoring for this event, details on the dataLayer
                  event should be shared in the table below.
                  <br></br>
                  For a quick start you can can copy / paste your JSON object
                  via the button below.
                </p>
              </div>

              {/* Buttons to edit template, add ecommerce event or clear template content. */}
              <Flex gap={10}>
                <ActionButton
                  text="back"
                  type=""
                  icon={<ArrowLeftOutlined />}
                  onClickFunction={() => setTemplateModalContentCode("main")}
                  width={120}
                />
                <DropdownMenu
                  text="add ecommerce event"
                  type=""
                  disabled={selectedTemplate?.length > 1}
                  menuProps={menuProps}
                  loading={false}
                  style={{ padding: "12px 20px" }}
                  icon={<DownOutlined />}
                />
                <ActionButton
                  text="clean json"
                  type=""
                  icon={<RedoOutlined />}
                  onClickFunction={clearJSON}
                />
              </Flex>

              {/* JSON object structure table */}
              <Space>
                <NestedObjectTable
                  key={selectedStepId}
                  data={selectedTemplateStr}
                  stepType={selectedStepType}
                  updateTemplateFunction={setSelectedTemplate}
                  setEditedEventTemplateNewVersion={setSelectedTemplate}
                />
              </Space>
            </div>

            {/* Save button for dataLayer object template */}
            <div className="drawer-buttons-area">
              <div className="drawer-buttons-area-shad"></div>
              <div className="drawer-buttons-area-button">
                <ActionButton
                  size="large"
                  type="primary"
                  width={140}
                  onClickFunction={() => handleSaveAction("templates")}
                >
                  Save
                </ActionButton>
              </div>
            </div>
          </Drawer>

          {/* Cookies acceptance scenario steps modal. */}
          <Drawer
            title={cookiesDrawerTitle}
            placement={defaultPlacement}
            onClose={() => {
              if (showCookiesUnsavedModal) {
                setShowCookiesUnsavedModal(false);
                return;
              }
              if (cookiesDirty) {
                setShowCookiesUnsavedModal(true);
                return;
              }
              closeCookiesDrawer();
            }}
            open={cookiesDrawerVisible}
            closable={true}
            width={thirdDrawerWidth}
            styles={{ header: { display: "none" } }}
          >
            <ConfirmationModalWindow
              open={cookiesStepsListChangeModalState}
              onOk={stepsListChangeModalOkFunction}
              onCancel={handleCookiesStepsListModalCancel}
              text={"Are you sure you want to update the steps list?"}
            />

            <div
              key={isConfigParamsUpdatedBeforeSave}
              className={`modal-elems-rows scenarios-modal-padding`}
            >
              <div>
                <h2 className="header-letter-spacing">
                  Cookies Acceptance Scenario Steps
                </h2>
              </div>

              {/* Toolbar. */}
              <Space size={25}>
                {/* Required switch — gates execution even when steps are
                    configured. */}
                <Space>
                  <Switch
                    checked={scenarioCookiesStatus}
                    onChange={(value) => setScenarioCookiesStatus(value)}
                  />
                  <label>
                    {scenarioCookiesStatus ? "Required" : "Not required"}
                  </label>
                </Space>
                <Space>
                  {/* Copy steps from another scenario */}
                  <DropdownMenu
                    text="Import steps"
                    menuProps={cookiesStepsScenarioOptions}
                    type="primary"
                    loading={false}
                    icon={<CopyOutlined />}
                    iconPosition="start"
                  />
                  {/* Clear steps list */}
                  <ActionButton
                    // type="saveChanges"
                    text={
                      <Space>
                        <ClearOutlined />
                        Clear steps
                      </Space>
                    }
                    onClickFunction={() =>
                      handleStepsListModalChangeState({
                        newState: true,
                        okFunction: () =>
                          clearStepsList({
                            listUpdateFunction: setScenarioCookiesSteps,
                            modalCancelFunction:
                              handleCookiesStepsListModalCancel,
                          }),
                        modalStateChangeFunction:
                          setCookiesStepsListChangeModalState,
                      })
                    }
                  />
                </Space>
              </Space>

              {/* Dynamically sortable steps list. */}
              <div style={{ paddingBottom: "5rem" }}>
                <h3 className="modal-category">Steps</h3>
                <div style={{ margin: "auto" }}>
                  <SortableList
                    items={scenarioCookiesSteps}
                    allowedStepTypes={["click_on_element"]}
                    handleDataLayerObjEdit={() => {}}
                    handleStepsUpdate={setScenarioCookiesSteps}
                    resolveComponentSteps={resolveComponentSteps}
                    resolveLinkedSteps={resolveLinkedSteps}
                    getBlockDisplayName={getBlockDisplayName}
                    onDetachBlock={(blockId) => {
                      const block = scenarioCookiesSteps.find(
                        (s) => s?.blockId === blockId,
                      );
                      requestDetachBlock(
                        block,
                        setScenarioCookiesSteps,
                        scenarioCookiesSteps,
                        "cookies",
                      );
                    }}
                    onDeleteBlock={(blockId) => {
                      const block = scenarioCookiesSteps.find(
                        (s) => s?.blockId === blockId,
                      );
                      requestDeleteBlock(
                        block,
                        setScenarioCookiesSteps,
                        scenarioCookiesSteps,
                        "cookies",
                      );
                    }}
                    onEditSource={handleEditBlockSource}
                  />
                </div>
              </div>
            </div>

            {/* Save button for scenario steps list */}
            <div className="drawer-buttons-area">
              <div className="drawer-buttons-area-shad"></div>
              <div className="drawer-buttons-area-button">
                <ActionButton
                  size="large"
                  type="primary"
                  width={140}
                  onClickFunction={() => handleSaveAction("cookies")}
                >
                  Save
                </ActionButton>
              </div>
            </div>

            {/* Unsaved-changes modal for cookies — mirrors the scenarios
                pattern (Save your changes / discard / save changes). */}
            <Modal
              open={showCookiesUnsavedModal}
              centered
              title={
                <div className="closeIconDiv">
                  <span>Save your changes</span>
                  <CloseOutlined
                    onClick={() => setShowCookiesUnsavedModal(false)}
                    className="closeIcon"
                  />
                </div>
              }
              onCancel={() => setShowCookiesUnsavedModal(false)}
              closable={false}
              footer={[
                <Button
                  key="discard"
                  type="default"
                  onClick={discardAndCloseCookies}
                >
                  discard
                </Button>,
                <Button
                  key="save"
                  type="saveChanges"
                  onClick={() => {
                    setShowCookiesUnsavedModal(false);
                    handleSaveAction("cookies");
                  }}
                >
                  save changes
                </Button>,
              ]}
            >
              <p>Do you want to save changes before leaving this step?</p>
            </Modal>
          </Drawer>

          {/* Login scenario steps modal. */}
          <Drawer
            className="scenarios-drawer"
            placement={defaultPlacement}
            onClose={() => {
              if (showLoginUnsavedModal) {
                setShowLoginUnsavedModal(false);
                return;
              }
              if (loginDirty) {
                setShowLoginUnsavedModal(true);
                return;
              }
              closeLoginDrawer();
            }}
            open={loginDrawerVisible}
            closeIcon={null}
            closable={true}
            width={firstDrawerWidth}
            styles={{ header: { display: "none" } }}
          >
            <ConfirmationModalWindow
              open={loginStepsListChangeModalState}
              onOk={stepsListChangeModalOkFunction}
              onCancel={handleLoginStepsListModalCancel}
              text={"Are you sure you want to update the steps list?"}
            />

            <div
              key={isConfigParamsUpdatedBeforeSave}
              className={`modal-elems-rows scenarios-modal-padding`}
            >
              <div>
                <h2 className="header-letter-spacing">
                  Set-up details for login scenario
                </h2>
              </div>
              {/* Docs banner removed (2026-05-13); the support_request link
                  remains accessible via the global footer / help menu. */}
              {false && (
              <div
                style={{ marginBottom: 0 }}
                className="container-guide modal-panel-button-row"
              >
                <Link
                  className="modal-guide-banner "
                  target="_blank"
                  to={pageScenariosDescLink}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div className="file-icon" style={{ color: "$cc-blue" }}>
                    <svg
                      width="30"
                      height="36"
                      viewBox="0 0 30 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g filter="url(#filter0_d_190_157)">
                        <path
                          d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z"
                          fill="#69C4FF"
                          fillOpacity="0.5"
                        />
                        <path
                          d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z"
                          fill="#69C4FF"
                          fillOpacity="0.5"
                        />
                        <path
                          d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z"
                          fill="#DBF1FF"
                        />
                        <path
                          d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z"
                          fill="#B0E0FF"
                        />
                        <path
                          d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z"
                          fill="white"
                        />
                        <line
                          x1="8.82252"
                          y1="9.40248"
                          x2="19.988"
                          y2="9.40248"
                          stroke="#009BFF"
                          strokeOpacity="0.14"
                          strokeWidth="0.389493"
                          strokeLinecap="round"
                        />
                        <line
                          x1="8.90306"
                          y1="15.1451"
                          x2="20.0685"
                          y2="15.1451"
                          stroke="#009BFF"
                          strokeOpacity="0.14"
                          strokeWidth="0.389493"
                          strokeLinecap="round"
                        />
                        <line
                          x1="8.82252"
                          y1="11.3499"
                          x2="19.988"
                          y2="11.3499"
                          stroke="#009BFF"
                          strokeOpacity="0.14"
                          strokeWidth="0.389493"
                          strokeLinecap="round"
                        />
                        <line
                          x1="8.90306"
                          y1="17.0925"
                          x2="20.0685"
                          y2="17.0925"
                          stroke="#009BFF"
                          strokeOpacity="0.14"
                          strokeWidth="0.389493"
                          strokeLinecap="round"
                        />
                        <line
                          x1="8.82252"
                          y1="13.1675"
                          x2="19.988"
                          y2="13.1675"
                          stroke="#009BFF"
                          strokeOpacity="0.14"
                          strokeWidth="0.389493"
                          strokeLinecap="round"
                        />
                        <path
                          d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z"
                          fill="#69C4FF"
                          fillOpacity="0.14"
                        />
                      </g>
                      <defs>
                        <filter
                          id="filter0_d_190_157"
                          x="0.255097"
                          y="0.170166"
                          width="24.9498"
                          height="30.5854"
                          filterUnits="userSpaceOnUse"
                          colorInterpolationFilters="sRGB"
                        >
                          <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                          />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dy="1.74496" />
                          <feGaussianBlur stdDeviation="0.872482" />
                          <feComposite in2="hardAlpha" operator="out" />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0"
                          />
                          <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_190_157"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_190_157"
                            result="shape"
                          />
                        </filter>
                      </defs>
                    </svg>
                  </div>
                  <div className="panel-button-text">
                    <div className="panel-button-desc">
                      Click here to follow the complete setup guide for the
                      DataLayer Guard.
                      <br /> Question not covered?{" "}
                      <span
                        role="link"
                        tabIndex={0}
                        className="support_request_link"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(
                            "https://portal.code-cube.io/support_request",
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(
                              "https://portal.code-cube.io/support_request",
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                      >
                        Please get in contact.
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
              )}
              <div className="modal-text">
                <p>
                  This page provides an overview of login scenario that
                  DataLayer Guard should follow to sussesfully login. Each step
                  should replicate the step taken by a typical user.
                </p>
              </div>
              {/* Scenario title and starting point. */}

              {/* Dynamically sortable steps list. */}
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <h3 className="modal-category">
                  Steps to take for successful login
                </h3>
                <div style={{ width: "100%" }}>
                  <SortableList
                    items={scenarioLoginSteps}
                    allowedStepTypes={["click_on_element"]}
                    handleDataLayerObjEdit={() => {}}
                    handleStepsUpdate={setScenarioLoginSteps}
                    resolveComponentSteps={resolveComponentSteps}
                    resolveLinkedSteps={resolveLinkedSteps}
                    getBlockDisplayName={getBlockDisplayName}
                    onDetachBlock={(blockId) => {
                      const block = scenarioLoginSteps.find(
                        (s) => s?.blockId === blockId,
                      );
                      requestDetachBlock(
                        block,
                        setScenarioLoginSteps,
                        scenarioLoginSteps,
                        "login",
                      );
                    }}
                    onDeleteBlock={(blockId) => {
                      const block = scenarioLoginSteps.find(
                        (s) => s?.blockId === blockId,
                      );
                      requestDeleteBlock(
                        block,
                        setScenarioLoginSteps,
                        scenarioLoginSteps,
                        "login",
                      );
                    }}
                    onEditSource={handleEditBlockSource}
                  />
                </div>
              </Space>
            </div>

            {/* Save button for scenario steps list */}
            <div className="drawer-buttons-area">
              <div className="drawer-buttons-area-shad"></div>
              <div className="drawer-buttons-area-button">
                <ActionButton
                  size="large"
                  type="primary"
                  width={140}
                  onClickFunction={() => handleSaveAction("login")}
                >
                  Save
                </ActionButton>
              </div>
            </div>

            {/* Unsaved-changes modal for login — mirrors the scenarios
                pattern (Save your changes / discard / save changes). */}
            <Modal
              open={showLoginUnsavedModal}
              centered
              title={
                <div className="closeIconDiv">
                  <span>Save your changes</span>
                  <CloseOutlined
                    onClick={() => setShowLoginUnsavedModal(false)}
                    className="closeIcon"
                  />
                </div>
              }
              onCancel={() => setShowLoginUnsavedModal(false)}
              closable={false}
              footer={[
                <Button
                  key="discard"
                  type="default"
                  onClick={discardAndCloseLogin}
                >
                  discard
                </Button>,
                <Button
                  key="save"
                  type="saveChanges"
                  onClick={() => {
                    setShowLoginUnsavedModal(false);
                    handleSaveAction("login");
                  }}
                >
                  save changes
                </Button>,
              ]}
            >
              <p>Do you want to save changes before leaving this step?</p>
            </Modal>
          </Drawer>
        </Drawer>
      </Drawer>
    </div>
  );
};

export default ScenariosMenu;
